import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import RefereePanel from './RefereePanel';
import PlayerSearchDropdown from './PlayerSearchDropdown';
import RandomMatchPanel from './RandomMatchPanel';
import { API_BASE } from "./config";
import '../css/GameMatch.css';
import socket from "../socket/socket";
import '../css/GameMatch.queue-row.css';
import '../css/GameMatch.dashboard.css';
import '../css/GameMatchMoveCourt.css';
import '../css/PlayerHistoryHover.css';

const getMemberStatus = player =>
  String(player?.memberStatus ?? player?.status ?? '')
    .trim()
    .toUpperCase();

const getHandLevel = player => {
  const savedRank = String(
    player?.level_rank ?? player?.rank ?? player?.hand_level ?? ''
  )
    .replace(/^มือ\s*/i, '')
    .trim()
    .toUpperCase();

  if (savedRank === 'S-P') return 'S/P';
  if (savedRank === 'N-S' || savedRank === 'NP') return 'N/S';
  if (savedRank === 'NB PLUS' || savedRank === 'NBPLUS') return 'NB+';
  if (savedRank === 'N MINUS' || savedRank === 'NMINUS') return 'N-';
  if (savedRank === 'S-') return 'S';
  if (savedRank) return savedRank;

  const point = Number(player?.point || 0);
  if (point >= 700) return 'S/P';
  if (point >= 650) return 'S';
  if (point >= 600) return 'N/S';
  if (point >= 550) return 'N';
  if (point >= 500) return 'N-';
  if (point >= 450) return 'NB+';
  if (point >= 400) return 'NB';
  if (point >= 350) return 'BGM';
  if (point >= 300) return 'BG';
  return '-';
};

const getQueuePlayers = match => [
  ...(match?.team1 || []),
  ...(match?.team2 || [])
];

const getMatchDisplayNo = match => {
  const groupMatchNo =
    Number(
      match?.groupMatchNo ??
      match?.group_match_no ??
      0
    );

  if (
    Number.isFinite(groupMatchNo) &&
    groupMatchNo > 0
  ) {
    return groupMatchNo;
  }

  return '-';
};

const MATCH_VOICE_STORAGE_KEY = 'badminton-match-voice-enabled-v2';
const GOOGLE_TTS_VOICE_STORAGE_KEY = 'badminton-google-tts-voice';
const DEFAULT_GOOGLE_TTS_VOICE = 'th-TH-Neural2-C';
const DEFAULT_GOOGLE_TTS_VOICES = [
  { name: 'th-TH-Neural2-C', label: 'ผู้หญิง Neural2-C' },
  { name: 'th-TH-Standard-A', label: 'ผู้หญิง Standard-A (ประหยัด)' },
  { name: 'th-TH-Chirp3-HD-Aoede', label: 'ผู้หญิง Aoede (Chirp HD)' },
  { name: 'th-TH-Chirp3-HD-Leda', label: 'ผู้หญิง Leda (Chirp HD)' },
  { name: 'th-TH-Chirp3-HD-Zephyr', label: 'ผู้หญิง Zephyr (Chirp HD)' },
  { name: 'th-TH-Chirp3-HD-Puck', label: 'ผู้ชาย Puck (Chirp HD)' },
  { name: 'th-TH-Chirp3-HD-Charon', label: 'ผู้ชาย Charon (Chirp HD)' },
  { name: 'th-TH-Chirp3-HD-Fenrir', label: 'ผู้ชาย Fenrir (Chirp HD)' },
  { name: 'th-TH-Chirp3-HD-Orus', label: 'ผู้ชาย Orus (Chirp HD)' }
];
const MATCH_VOICE_RATE = 0.5;
const MATCH_NAME_VOICE_RATE = 0.35;
const MATCH_VOICE_PITCH = 1;
const MATCH_VOICE_FALLBACK_PITCH = 1.05;
const MATCH_CHIME_DELAY_MS = 650;
const RECENT_MATCH_HIGHLIGHT_MS = 2 * 60 * 1000;
const NATURAL_THAI_VOICE_HINTS = [
  'natural',
  'neural',
  'online'
];
const FEMALE_THAI_VOICE_HINTS = [
  'premwadee',
  'achara',
  'kanya',
  'google ภาษาไทย',
  'google thai',
  'female',
  'ผู้หญิง',
  'หญิง'
];
const activeSpeechUtterances = new Set();
let speechScheduleVersion = 0;
let speechStartDelayUntil = 0;
let speechScheduleOrder = 0;
let announcementAudioContext = null;

const formatAnnouncementName = player => {
  const rawName = String(player?.name || '').trim();
  if (!rawName) return 'ผู้เล่น';

  return `คุณ ${rawName.replace(/^คุณ\s*/i, '')}`;
};

const buildTeamAnnouncementText = (players, fallbackName) => {
  const names = (players || []).map(formatAnnouncementName);

  if (names.length === 0) {
    return fallbackName;
  }

  return names.join(' กับ ');
};

const buildMatchStartAnnouncement = (match, startData = {}) => {
  const matchNo =
    startData.groupMatchNo ||
    startData.group_match_no ||
    getMatchDisplayNo(match);
  const courtNo =
    startData.courtNo ||
    startData.court_no ||
    match?.courtNo ||
    match?.court_no ||
    '-';
  const team1Names = buildTeamAnnouncementText(
    match?.team1,
    'ทีมหนึ่ง'
  );
  const team2Names = buildTeamAnnouncementText(
    match?.team2,
    'ทีมสอง'
  );

  return [
    { text: `แมตช์ที่ ${matchNo}`, rate: MATCH_VOICE_RATE },
    { text: team1Names, rate: MATCH_NAME_VOICE_RATE },
    { text: 'พบกับ', rate: MATCH_VOICE_RATE },
    { text: team2Names, rate: MATCH_NAME_VOICE_RATE },
    { text: `ที่สนาม ${courtNo}`, rate: MATCH_VOICE_RATE }
  ];
};

const getAnnouncementAudioContext = () => {
  if (typeof window === 'undefined') return false;

  const AudioContextClass =
    window.AudioContext || window.webkitAudioContext;

  if (!AudioContextClass) return false;

  try {
    if (
      !announcementAudioContext ||
      announcementAudioContext.state === 'closed'
    ) {
      announcementAudioContext = new AudioContextClass();
    }

    return announcementAudioContext;
  } catch {
    return false;
  }
};

const unlockAnnouncementAudio = () => {
  const audioContext = getAnnouncementAudioContext();
  if (!audioContext) return false;

  audioContext.resume().catch(() => {});
  return true;
};

const playAnnouncementChime = () => {
  const audioContext = getAnnouncementAudioContext();
  if (!audioContext) return false;

  try {
    const startAt = audioContext.currentTime + 0.02;
    const notes = [
      { frequency: 659.25, offset: 0, duration: 0.3 },
      { frequency: 880, offset: 0.18, duration: 0.4 }
    ];

    audioContext.resume().catch(() => {});

    notes.forEach(note => {
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const noteStart = startAt + note.offset;
      const noteEnd = noteStart + note.duration;

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(note.frequency, noteStart);
      gain.gain.setValueAtTime(0.0001, noteStart);
      gain.gain.exponentialRampToValueAtTime(0.16, noteStart + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, noteEnd);

      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start(noteStart);
      oscillator.stop(noteEnd);
    });

    return true;
  } catch {
    return false;
  }
};

const selectThaiVoice = voices => {
  const thaiVoices = (voices || []).filter(voice =>
    /^th(?:-|_)/i.test(String(voice.lang || ''))
  );
  const getVoiceInfo = voice => {
    const voiceName = String(voice.name || '').toLowerCase();
    const isFemaleVoice = FEMALE_THAI_VOICE_HINTS.some(hint =>
      voiceName.includes(hint)
    );
    const isNaturalVoice = NATURAL_THAI_VOICE_HINTS.some(hint =>
      voiceName.includes(hint)
    );

    return {
      voice,
      isFemaleVoice,
      score:
        (isFemaleVoice ? 100 : 0) +
        (isNaturalVoice ? 50 : 0) +
        (voice.default ? 5 : 0)
    };
  };
  const selectedVoice = thaiVoices
    .map(getVoiceInfo)
    .sort((a, b) => b.score - a.score)[0];

  return {
    voice: selectedVoice?.voice || null,
    isFemaleVoice: Boolean(selectedVoice?.isFemaleVoice)
  };
};

const speakThaiAnnouncement = (
  announcement,
  { clearPending = false } = {}
) => {
  if (
    typeof window === 'undefined' ||
    !window.speechSynthesis ||
    !window.SpeechSynthesisUtterance
  ) {
    return false;
  }

  const synthesis = window.speechSynthesis;
  const voices = synthesis.getVoices();
  const {
    voice: thaiVoice,
    isFemaleVoice
  } = selectThaiVoice(voices);
  const segments = (Array.isArray(announcement)
    ? announcement
    : [{ text: announcement, rate: MATCH_VOICE_RATE }]
  ).filter(segment => String(segment?.text || '').trim());
  const utterances = segments.map(segment => {
    const utterance = new window.SpeechSynthesisUtterance(
      String(segment.text).trim()
    );

    utterance.lang = 'th-TH';
    utterance.rate = Number(segment.rate) || MATCH_VOICE_RATE;
    utterance.pitch = isFemaleVoice
      ? MATCH_VOICE_PITCH
      : MATCH_VOICE_FALLBACK_PITCH;
    utterance.volume = 1;

    if (thaiVoice) {
      utterance.voice = thaiVoice;
    }

    return utterance;
  });

  if (utterances.length === 0) return false;

  if (clearPending) {
    synthesis.cancel();
    activeSpeechUtterances.clear();
    speechScheduleVersion += 1;
    speechScheduleOrder = 0;
    playAnnouncementChime();
    speechStartDelayUntil = Date.now() + MATCH_CHIME_DELAY_MS;
  }

  const currentScheduleVersion = speechScheduleVersion;
  const currentScheduleOrder = speechScheduleOrder;
  speechScheduleOrder += 1;
  utterances.forEach(utterance => {
    const releaseUtterance = () => {
      activeSpeechUtterances.delete(utterance);
    };

    utterance.onend = releaseUtterance;
    utterance.onerror = releaseUtterance;
    activeSpeechUtterances.add(utterance);
  });

  const startSpeaking = () => {
    if (currentScheduleVersion !== speechScheduleVersion) {
      utterances.forEach(utterance => {
        activeSpeechUtterances.delete(utterance);
      });
      return;
    }

    const latestVoiceSelection = selectThaiVoice(
      synthesis.getVoices()
    );

    utterances.forEach(utterance => {
      if (latestVoiceSelection.voice) {
        utterance.voice = latestVoiceSelection.voice;
        utterance.pitch = latestVoiceSelection.isFemaleVoice
          ? MATCH_VOICE_PITCH
          : MATCH_VOICE_FALLBACK_PITCH;
      }

      synthesis.speak(utterance);
    });
  };
  const remainingDelay = Math.max(
    0,
    speechStartDelayUntil - Date.now()
  ) + currentScheduleOrder * 20;

  if (remainingDelay > 0) {
    window.setTimeout(startSpeaking, remainingDelay);
  } else {
    startSpeaking();
  }

  return true;
};

const speakThaiAnnouncementTwice = (
  announcement,
  { clearPending = false } = {}
) => {
  const firstRoundStarted = speakThaiAnnouncement(
    announcement,
    { clearPending }
  );

  if (!firstRoundStarted) return false;

  speakThaiAnnouncement(announcement);
  return true;
};

let googleAnnouncementQueue = Promise.resolve();
let googleAnnouncementVersion = 0;
let googleBrowserFallbackVersion = null;
let activeGoogleAudioCancel = null;

const waitForAnnouncement = duration => new Promise(resolve => {
  window.setTimeout(resolve, Math.max(0, Number(duration) || 0));
});

const resetAnnouncementPlayback = ({ playChime = true } = {}) => {
  googleAnnouncementVersion += 1;
  googleBrowserFallbackVersion = null;
  googleAnnouncementQueue = Promise.resolve();

  if (activeGoogleAudioCancel) {
    activeGoogleAudioCancel();
    activeGoogleAudioCancel = null;
  }

  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }

  activeSpeechUtterances.clear();
  speechScheduleVersion += 1;
  speechScheduleOrder = 0;
  if (playChime) {
    playAnnouncementChime();
    speechStartDelayUntil = Date.now() + MATCH_CHIME_DELAY_MS;
  } else {
    speechStartDelayUntil = 0;
  }
};

const buildGoogleMatchSpeechPayload = (
  match,
  startData = {},
  voiceName = DEFAULT_GOOGLE_TTS_VOICE
) => ({
  matchNo:
    startData.groupMatchNo ||
    startData.group_match_no ||
    getMatchDisplayNo(match),
  courtNo:
    startData.courtNo ||
    startData.court_no ||
    match?.courtNo ||
    match?.court_no ||
    '',
  team1: (match?.team1 || [])
    .map(player => String(player?.name || '').trim())
    .filter(Boolean),
  team2: (match?.team2 || [])
    .map(player => String(player?.name || '').trim())
    .filter(Boolean),
  voiceName
});

const requestGoogleMatchSpeech = async payload => {
  const response = await fetch(`${API_BASE}/api/speech/match-start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody.message ||
      `Google TTS ตอบกลับ HTTP ${response.status}`
    );
  }

  return response.blob();
};

const playGoogleSpeechAudio = blob => new Promise((resolve, reject) => {
  const audioUrl = URL.createObjectURL(blob);
  const audio = new window.Audio(audioUrl);
  let settled = false;

  const finish = (callback, value) => {
    if (settled) return;
    settled = true;

    audio.onended = null;
    audio.onerror = null;
    URL.revokeObjectURL(audioUrl);

    if (activeGoogleAudioCancel === cancelPlayback) {
      activeGoogleAudioCancel = null;
    }

    callback(value);
  };
  const cancelPlayback = () => {
    audio.pause();
    audio.removeAttribute('src');
    finish(resolve, false);
  };

  activeGoogleAudioCancel = cancelPlayback;
  audio.preload = 'auto';
  audio.volume = 1;
  audio.onended = () => finish(resolve, true);
  audio.onerror = () => finish(
    reject,
    new Error('เบราว์เซอร์เล่นเสียง Google ไม่สำเร็จ')
  );

  audio.play().catch(error => finish(reject, error));
});

const enqueueGoogleMatchAnnouncement = (
  match,
  startData,
  {
    clearPending = false,
    voiceName = DEFAULT_GOOGLE_TTS_VOICE
  } = {}
) => {
  if (
    typeof window === 'undefined' ||
    typeof window.fetch !== 'function' ||
    typeof window.Audio !== 'function'
  ) {
    return false;
  }

  if (clearPending) {
    resetAnnouncementPlayback();
  }

  const queueVersion = googleAnnouncementVersion;
  const fallbackAnnouncement = buildMatchStartAnnouncement(
    match,
    startData
  );
  const payload = buildGoogleMatchSpeechPayload(
    match,
    startData,
    voiceName
  );

  googleAnnouncementQueue = googleAnnouncementQueue
    .catch(() => {})
    .then(async () => {
      if (queueVersion !== googleAnnouncementVersion) return;

      if (googleBrowserFallbackVersion === queueVersion) {
        speakThaiAnnouncementTwice(fallbackAnnouncement);
        return;
      }

      try {
        const audioBlob = await requestGoogleMatchSpeech(payload);

        if (queueVersion !== googleAnnouncementVersion) return;

        const chimeDelay = Math.max(
          0,
          speechStartDelayUntil - Date.now()
        );

        if (chimeDelay > 0) {
          await waitForAnnouncement(chimeDelay);
        }

        if (queueVersion !== googleAnnouncementVersion) return;

        await playGoogleSpeechAudio(audioBlob);
        await waitForAnnouncement(250);

        if (queueVersion !== googleAnnouncementVersion) return;

        await playGoogleSpeechAudio(audioBlob);
      } catch (error) {
        if (queueVersion !== googleAnnouncementVersion) return;

        console.warn(
          'Google TTS ใช้งานไม่ได้ กำลังใช้ Web Speech สำรอง:',
          error
        );
        googleBrowserFallbackVersion = queueVersion;
        speakThaiAnnouncementTwice(fallbackAnnouncement);
      }
    });

  return true;
};

const getQueueBlockers = match =>
  getQueuePlayers(match).filter(player =>
    !['ACTIVE', 'QUEUE'].includes(getMemberStatus(player))
  );

const getHistoryCount = (historyMap, playerAId, playerBId) => {
  if (!historyMap || playerAId == null || playerBId == null) return 0;

  const directKey = `${playerAId}-${playerBId}`;
  const reverseKey = `${playerBId}-${playerAId}`;
  return Number(historyMap[directKey] ?? historyMap[reverseKey] ?? 0) || 0;
};

const getTotalMatches = player =>
  Number(
    player?.totalMatches ??
    player?.total_matches ??
    player?.total_games ??
    0
  ) || 0;

const GAMES_MONITOR_PAGE_SIZE = 8;

const HAND_LEVEL_ORDER = [
  'S/P',
  'S',
  'N/S',
  'N',
  'N-',
  'NB+',
  'NB',
  'BGM',
  'BG',
  '-'
];

const DAILY_PLAY_STYLE_LABELS = {
  FAST_POWER_MIX: 'ทั่วไป',
  TIGHT_MEDIUM_HEAVY: 'ขอเหนื่อย',
  ALL_ROUND_READY: 'ได้หมด พร้อมชน',
  ENJOY_FUN: 'เน้นฮาและเหนื่อย'
};

const getDailyPlayStyleLabel = player =>
  DAILY_PLAY_STYLE_LABELS[player?.daily_play_style] || 'ยังไม่เลือก Mood';

const parseMemberTime = value => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const getMemberArrivalTime = player =>
  parseMemberTime(
    player?.checkin_time ||
    player?.active_since ||
    player?.last_finished_at
  );

const getMemberWaitingTime = player =>
  parseMemberTime(
    player?.last_finished_at ||
    player?.active_since ||
    player?.checkin_time
  );

const formatElapsedTime = (startedAt, now) => {
  if (!Number.isFinite(startedAt)) return '-';

  const minutes = Math.max(0, Math.floor((now - startedAt) / 60000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const remainingMinutes = minutes % 60;

  if (days > 0) return `${days} วัน ${hours} ชม.`;
  if (hours > 0) return `${hours} ชม. ${remainingMinutes} นาที`;
  return `${minutes} นาที`;
};

const getMatchStartTimestamp = match =>
  parseMemberTime(
    match?.start_time ||
    match?.startTime ||
    match?.started_at ||
    match?.startedAt
  );

const formatPlayingDuration = (startedAt, now) => {
  if (!Number.isFinite(startedAt)) return '--:--';

  const totalSeconds = Math.max(
    0,
    Math.floor((now - startedAt) / 1000)
  );
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');

  return hours > 0
    ? `${hours}:${paddedMinutes}:${paddedSeconds}`
    : `${paddedMinutes}:${paddedSeconds}`;
};

const getPlayingTimeStage = elapsedMs => {
  if (!Number.isFinite(elapsedMs)) return 'unknown';

  const elapsedMinutes = elapsedMs / 60000;

  if (elapsedMinutes >= 20) return 'over-20';
  if (elapsedMinutes >= 15) return 'over-15';
  if (elapsedMinutes >= 10) return 'over-10';
  if (elapsedMinutes >= 5) return 'over-5';
  return 'fresh';
};

// เก็บ helpers สำหรับ UI เวลาการแข่งขันรุ่น compact ที่อาจเปิดใช้อีกครั้ง
void formatPlayingDuration;
void getPlayingTimeStage;

function PlayerHistoryHover({
  player,
  allPlayers = [],
  pairMap = {},
  opponentMap = {},
  className = ''
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const playerProfile =
    allPlayers.find(
      item => Number(item.user_id) === Number(player?.user_id)
    ) || player;

  const relationPlayers = allPlayers.filter(
    item => Number(item.user_id) !== Number(player?.user_id)
  );

  const partners = relationPlayers
    .map(item => ({
      ...item,
      count: getHistoryCount(pairMap, player?.user_id, item.user_id)
    }))
    .filter(item => item.count > 0)
    .sort(
      (a, b) =>
        b.count - a.count ||
        String(a.name || '').localeCompare(String(b.name || ''), 'th')
    );

  const opponents = relationPlayers
    .map(item => ({
      ...item,
      count: getHistoryCount(opponentMap, player?.user_id, item.user_id)
    }))
    .filter(item => item.count > 0)
    .sort(
      (a, b) =>
        b.count - a.count ||
        String(a.name || '').localeCompare(String(b.name || ''), 'th')
    );

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const cardWidth = Math.min(360, window.innerWidth - 24);
    const left = Math.min(
      Math.max(12, rect.left),
      Math.max(12, window.innerWidth - cardWidth - 12)
    );
    const showAbove = window.innerHeight - rect.bottom < 330 && rect.top > 330;

    setPosition(
      showAbove
        ? { left, bottom: window.innerHeight - rect.top + 8 }
        : { left, top: rect.bottom + 8 }
    );
  }, []);

  const showHistory = useCallback(() => {
    cancelClose();
    updatePosition();
    setOpen(true);
  }, [cancelClose, updatePosition]);

  const hideHistorySoon = useCallback(() => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 160);
  }, [cancelClose]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  useEffect(() => {
    if (!open) return undefined;

    const reposition = () => updatePosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);

    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, updatePosition]);

  if (!player) return null;

  return (
    <>
      <span
        ref={triggerRef}
        className="ph-player-history-control"
        onMouseEnter={showHistory}
        onMouseLeave={hideHistorySoon}
      >
        <span
          className={`ph-player-trigger ${className}`.trim()}
          tabIndex={0}
          role="button"
          aria-label={`ดูประวัติของ ${player.name}`}
          aria-expanded={open}
          onFocus={showHistory}
          onBlur={hideHistorySoon}
          onClick={showHistory}
          onKeyDown={event => {
            if (event.key === 'Escape') setOpen(false);
          }}
        >
          {player.name}
        </span>

        <button
          type="button"
          className="ph-history-open-button"
          aria-label={`แสดงประวัติของ ${player.name}`}
          aria-expanded={open}
          onFocus={showHistory}
          onBlur={hideHistorySoon}
          onClick={showHistory}
        >
          <span aria-hidden="true">📋</span>
          <span>ประวัติ</span>
        </button>
      </span>

      {open && position && createPortal(
        <aside
          className="ph-history-card"
          style={position}
          role="tooltip"
          onMouseEnter={cancelClose}
          onMouseLeave={hideHistorySoon}
        >
          <div className="ph-history-header">
            <span className="ph-history-avatar">
              {String(playerProfile?.name || '?').trim().charAt(0)}
            </span>
            <div className="ph-history-title">
              <strong>{playerProfile?.name}</strong>
              <span>ประวัติการเล่นในกลุ่ม</span>
            </div>
            <button
              type="button"
              className="ph-history-close"
              aria-label="ปิดประวัติ"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>

          <div className="ph-history-summary">
            <span>🎮 {getTotalMatches(playerProfile)} เกม</span>
            <span>🏸 มือ {getHandLevel(playerProfile)}</span>
            <span>⭐ {Number(playerProfile?.point || 0)} คะแนน</span>
          </div>

          <div className="ph-history-sections">
            <section className="ph-relation-section partner">
              <h4>🤝 คนที่เคยคู่กัน</h4>
              {partners.length === 0 ? (
                <p className="ph-history-empty">ยังไม่มีประวัติคู่กัน</p>
              ) : (
                <div className="ph-relation-list">
                  {partners.map(item => (
                    <div className="ph-relation-row" key={item.user_id}>
                      <span>
                        <strong>{item.name}</strong>
                        <small>มือ {getHandLevel(item)}</small>
                      </span>
                      <b>{item.count} ครั้ง</b>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="ph-relation-section opponent">
              <h4>⚔️ คนที่เคยอยู่ฝั่งตรงข้าม</h4>
              {opponents.length === 0 ? (
                <p className="ph-history-empty">ยังไม่มีประวัติฝั่งตรงข้าม</p>
              ) : (
                <div className="ph-relation-list">
                  {opponents.map(item => (
                    <div className="ph-relation-row" key={item.user_id}>
                      <span>
                        <strong>{item.name}</strong>
                        <small>มือ {getHandLevel(item)}</small>
                      </span>
                      <b>{item.count} ครั้ง</b>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </aside>,
        document.body
      )}
    </>
  );
}

function GameMatch({ groupId, displayMode = 'full' }) {

  const isMonitorBoard = displayMode === 'monitor-board';

  /* =========================
     STATE
  ========================== */

  const [queue, setQueue] = useState([]);
  const [matches, setMatches] = useState([]);
  const [courts, setCourts] = useState([]);
  const [lockedPairs, setLockedPairs] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);

  const [lockA, setLockA] = useState(null);
  const [lockB, setLockB] = useState(null);

  const [startingId, setStartingId] = useState(null);
  const [bulkQueueAction, setBulkQueueAction] = useState(null);
  const [autoCourtEnabled, setAutoCourtEnabled] = useState(() => {
    try { return window.localStorage.getItem('gameMatchAutoCourt') === 'true'; }
    catch { return false; }
  });
  const [selectedEndMatchIds, setSelectedEndMatchIds] = useState([]);
  const [bulkEndingMatches, setBulkEndingMatches] = useState(false);
  const [bulkEndMessage, setBulkEndMessage] = useState(null);
  const [matchVoiceEnabled, setMatchVoiceEnabled] = useState(() => {
    try {
      return window.localStorage.getItem(MATCH_VOICE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [googleTtsVoice, setGoogleTtsVoice] = useState(() => {
    try {
      return window.localStorage.getItem(GOOGLE_TTS_VOICE_STORAGE_KEY) ||
        DEFAULT_GOOGLE_TTS_VOICE;
    } catch {
      return DEFAULT_GOOGLE_TTS_VOICE;
    }
  });
  const [googleTtsVoices, setGoogleTtsVoices] = useState(
    DEFAULT_GOOGLE_TTS_VOICES
  );

  const [editingQueueId, setEditingQueueId] = useState(null);
  const [editQueueTeams, setEditQueueTeams] = useState({ team1: [], team2: [] });

  const [editingMatchId, setEditingMatchId] = useState(null);
  const [editTeams, setEditTeams] = useState({ team1: [], team2: [] });
  const [swappingPairKey, setSwappingPairKey] = useState(null);
  const [movingCourtMatch, setMovingCourtMatch] = useState(null);
  const [targetCourtNo, setTargetCourtNo] = useState(null);
  const [movingCourt, setMovingCourt] = useState(false);
  const [overviewNow, setOverviewNow] = useState(Date.now());
  const [gamesMonitorKeyword, setGamesMonitorKeyword] = useState('');
  const [gamesMonitorPage, setGamesMonitorPage] = useState(1);
  const [memberOverviewVisible, setMemberOverviewVisible] = useState(false);
  const [floatingMemberModal, setFloatingMemberModal] = useState(null);
  const [floatingMemberMenuOpen, setFloatingMemberMenuOpen] = useState(true);
  const [playingNow, setPlayingNow] = useState(Date.now());

  useEffect(() => {
    try { window.localStorage.setItem('gameMatchAutoCourt', String(autoCourtEnabled)); }
    catch { /* ใช้งานต่อได้แม้ localStorage ถูกปิด */ }
  }, [autoCourtEnabled]);

  useEffect(() => {
    if (!movingCourtMatch) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = event => {
      if (event.key === 'Escape' && !movingCourt) {
        setMovingCourtMatch(null);
        setTargetCourtNo(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [movingCourt, movingCourtMatch]);
  /* =========================
     API HELPER
  ========================== */
  const [opponentMap, setOpponentMap] = useState({});

  const playingMatchesForBulk = useMemo(
    () => matches.filter(match => match.status === 'playing'),
    [matches]
  );

/* =========================================================
   PLAYING MATCH SORT + RECENT HIGHLIGHT
========================================================= */

const sortedPlayingMatches = useMemo(() => {

  return matches
    .filter(match =>
      String(match?.status || '').toLowerCase() === 'playing'
    )
    .sort((a, b) => {

      /*
       * เรียง Court จากน้อย -> มาก
       * Court 1, 2, 3, 4 ...
       */

      const courtA =
        Number(
          a?.court_no ??
          a?.courtNo ??
          9999
        );

      const courtB =
        Number(
          b?.court_no ??
          b?.courtNo ??
          9999
        );

      if (courtA !== courtB) {
        return courtA - courtB;
      }

      /*
       * ถ้า Court เท่ากัน
       * ให้เรียง Match No จากน้อย -> มาก
       */

      return (
        Number(getMatchDisplayNo(a) || 999999) -
        Number(getMatchDisplayNo(b) || 999999)
      );

    });

}, [matches]);


/* =========================================================
   CHECK RECENT MATCH
   Highlight 2 นาทีหลัง Start
========================================================= */

const isRecentlyStartedMatch = useCallback(
  match => {

    const startedAt =
      getMatchStartTimestamp(match);

    if (!Number.isFinite(startedAt)) {
      return false;
    }

    const elapsed =
      playingNow - startedAt;

    return (
      elapsed >= 0 &&
      elapsed <= RECENT_MATCH_HIGHLIGHT_MS
    );

  },
  [playingNow]
);

  const selectedPlayingMatches = useMemo(() => {
    const selectedIds = new Set(selectedEndMatchIds.map(String));
    return playingMatchesForBulk.filter(match =>
      selectedIds.has(String(match.matchId))
    );
  }, [playingMatchesForBulk, selectedEndMatchIds]);

  const allPlayingMatchesSelected =
    playingMatchesForBulk.length > 0 &&
    selectedPlayingMatches.length === playingMatchesForBulk.length;

  useEffect(() => {
    const playingIds = new Set(
      playingMatchesForBulk.map(match => String(match.matchId))
    );

    setSelectedEndMatchIds(currentIds => {
      const nextIds = currentIds.filter(matchId =>
        playingIds.has(String(matchId))
      );

      return nextIds.length === currentIds.length ? currentIds : nextIds;
    });
  }, [playingMatchesForBulk]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        MATCH_VOICE_STORAGE_KEY,
        String(matchVoiceEnabled)
      );
    } catch {
      // localStorage อาจถูกปิด แต่เสียงประกาศยังใช้งานในหน้านี้ได้
    }
  }, [matchVoiceEnabled]);

  useEffect(() => {
    if (!matchVoiceEnabled) {
      resetAnnouncementPlayback({ playChime: false });
    }
  }, [matchVoiceEnabled]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        GOOGLE_TTS_VOICE_STORAGE_KEY,
        googleTtsVoice
      );
    } catch {
      // หาก localStorage ใช้ไม่ได้ จะจำเสียงไว้เฉพาะรอบที่เปิดหน้านี้
    }
  }, [googleTtsVoice]);

  const recentMembers = useMemo(
    () => [...allPlayers]
      .sort((left, right) => (
        (getMemberArrivalTime(right) || 0) -
        (getMemberArrivalTime(left) || 0)
      ))
      .slice(0, 6),
    [allPlayers]
  );

  const longestWaitingMembers = useMemo(
    () => allPlayers
      .filter(player => getMemberStatus(player) === 'ACTIVE')
      .sort((left, right) => (
        (getMemberWaitingTime(left) ?? Number.MAX_SAFE_INTEGER) -
        (getMemberWaitingTime(right) ?? Number.MAX_SAFE_INTEGER)
      ))
      .slice(0, 6),
    [allPlayers]
  );

  const gamesMonitorPlayers = useMemo(() => {
    const keyword = gamesMonitorKeyword.trim().toLowerCase();

    return [...allPlayers]
      .filter(player =>
        !keyword || String(player.name || '').toLowerCase().includes(keyword)
      )
      .sort((left, right) =>
        getTotalMatches(left) - getTotalMatches(right) ||
        String(left.name || '').localeCompare(String(right.name || ''), 'th')
      );
  }, [allPlayers, gamesMonitorKeyword]);

  const gamesMonitorTotalPages = Math.max(
    1,
    Math.ceil(gamesMonitorPlayers.length / GAMES_MONITOR_PAGE_SIZE)
  );
  const gamesMonitorStartIndex =
    (gamesMonitorPage - 1) * GAMES_MONITOR_PAGE_SIZE;
  const gamesMonitorPagePlayers = useMemo(
    () => gamesMonitorPlayers.slice(
      gamesMonitorStartIndex,
      gamesMonitorStartIndex + GAMES_MONITOR_PAGE_SIZE
    ),
    [gamesMonitorPlayers, gamesMonitorStartIndex]
  );

  useEffect(() => {
    setGamesMonitorPage(currentPage =>
      Math.min(currentPage, gamesMonitorTotalPages)
    );
  }, [gamesMonitorTotalPages]);

  const activeMemberCount = useMemo(
    () => allPlayers.filter(
      player => getMemberStatus(player) === 'ACTIVE'
    ).length,
    [allPlayers]
  );

  const handLevelSummary = useMemo(() => {
    const counts = new Map(
      HAND_LEVEL_ORDER.map(level => [level, 0])
    );

    allPlayers.forEach(player => {
      const level = getHandLevel(player);
      counts.set(level, Number(counts.get(level) || 0) + 1);
    });

    const extraLevels = [...counts.keys()]
      .filter(level => !HAND_LEVEL_ORDER.includes(level))
      .sort((left, right) => left.localeCompare(right, 'th'));

    return [...HAND_LEVEL_ORDER, ...extraLevels].map(level => ({
      level,
      count: Number(counts.get(level) || 0)
    }));
  }, [allPlayers]);

  const api = useCallback(async (url, method = 'GET', body) => {
    const res = await fetch(`${API_BASE}${url}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = new Error(data.message || `HTTP ${res.status}`);
      error.status = res.status;
      error.data = data;
      throw error;
    }
    return data;
  }, []);

  useEffect(() => {
    let active = true;

    const loadGoogleVoiceConfig = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/speech/config`);
        const config = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(config.message || `HTTP ${response.status}`);
        }

        if (!active) return;

        const voices = Array.isArray(config?.voices) && config.voices.length > 0
          ? config.voices
          : DEFAULT_GOOGLE_TTS_VOICES;
        const allowedNames = new Set(
          voices.map(voice => String(voice?.name || '').trim())
        );
        const serverVoice = allowedNames.has(config?.voice)
          ? config.voice
          : DEFAULT_GOOGLE_TTS_VOICE;

        setGoogleTtsVoices(voices);
        setGoogleTtsVoice(currentVoice => (
          allowedNames.has(currentVoice) ? currentVoice : serverVoice
        ));
      } catch (error) {
        console.warn('โหลดรายชื่อเสียง Google ไม่สำเร็จ:', error);
      }
    };

    loadGoogleVoiceConfig();

    return () => {
      active = false;
    };
  }, []);

  /* =========================
     LOAD DATA
  ========================== */

  const loadMatches = useCallback(async () => {
    setMatches(await api(`/api/game-match/group/${groupId}`));
  }, [api, groupId]);

  const [pairMap, setPairMap] = useState({});

  const loadQueue = useCallback(async () => {
    const data = await api(`/api/game-match/queue/${groupId}`);

    setQueue(Array.isArray(data.matches) ? data.matches : []);
    setPairMap(data.pairMap || {});
    setOpponentMap(data.opponentMap || {});
  }, [api, groupId]);

  const loadCourts = useCallback(async () => {
    setCourts(await api(`/api/courts/${groupId}/all`));
  }, [api, groupId]);

  const loadLockedPairs = useCallback(async () => {
    setLockedPairs(await api(`/api/game-match/locked-pairs/${groupId}`));
  }, [api, groupId]);

  const loadPlayers = useCallback(async () => {
    const data = await api(`/api/game-match/group/${groupId}/players`);
    const players = Array.isArray(data) ? data : [];

    setAllPlayers(currentPlayers => {
      const currentById = new Map(
        currentPlayers.map(player => [Number(player.user_id), player])
      );
      const isInitialLoad = currentPlayers.length === 0;
      const detectedAt = new Date().toISOString();

      return players.map(player => {
        const playerId = Number(player.user_id);
        const currentPlayer = currentById.get(playerId);
        const detectedAsNew = !isInitialLoad && !currentPlayer;

        return {
          ...currentPlayer,
          ...player,
          user_id: playerId,
          checkin_time:
            player.checkin_time ||
            currentPlayer?.checkin_time ||
            (detectedAsNew ? detectedAt : null),
          active_since:
            player.active_since ||
            currentPlayer?.active_since ||
            (detectedAsNew ? detectedAt : null)
        };
      });
    });
  }, [api, groupId]);

  useEffect(() => {
    if (!groupId) return;

    const handleConnect = () => {
      console.log("📡 JOIN GROUP:", groupId);
      socket.emit("join-group", Number(groupId));

      // ถ้า Socket หลุดตอนเพิ่มผู้เล่น ให้ดึงรายชื่อล่าสุดหลังเชื่อมต่อกลับมา
      loadPlayers().catch(error => {
        console.error("LOAD PLAYERS AFTER SOCKET CONNECT ERROR:", error);
      });
    };

    // ต้องลงทะเบียน connect listener เสมอ เพื่อให้ทำงานตอน reconnect ด้วย
    socket.on("connect", handleConnect);

    if (socket.connected) {
      handleConnect();
    }

    // 🔥 handler กลาง (ดีที่สุด)
    const isCurrentGroupEvent = (data = {}) => {
      const eventGroupId = data.groupId ?? data.group_id;
      if (eventGroupId !== undefined && eventGroupId !== null) {
        return Number(eventGroupId) === Number(groupId);
      }

      const eventGroupIds = Array.isArray(data.groupIds)
        ? data.groupIds.map(Number)
        : [];

      return eventGroupIds.length === 0 || eventGroupIds.includes(Number(groupId));
    };

    const handler = (data, type) => {
      if (!isCurrentGroupEvent(data)) return;

      console.log("?? SOCKET:", type, data);

      if (type === "queue-updated") {
        loadQueue();
        loadPlayers();
      }

      if (type === "match-started") {
        loadQueue();
        loadMatches();
        loadPlayers();
        loadCourts();
      }

      if (type === "match-ended") {
        loadQueue();
        loadMatches();
        loadPlayers();
        loadCourts();
      }

      if (type === "match-updated") {
        loadQueue();
        loadMatches();
        loadPlayers();
        loadCourts();
      }
    };

    const handleMatchUpdated = data => handler(data, "match-updated");
    const handleQueueUpdated = data => handler(data, "queue-updated");
    const handleMatchStarted = data => handler(data, "match-started");
    const handleMatchEnded = data => handler(data, "match-ended");
    const handlePlayerStatusUpdated = (data = {}) => {
      if (!isCurrentGroupEvent(data)) return;
      loadPlayers().catch(error => {
        console.error("LOAD PLAYERS AFTER STATUS UPDATE ERROR:", error);
      });
    };

    // เมื่อมีสมาชิกใหม่เข้ากลุ่ม ให้ดึงรายชื่อผู้เล่นล่าสุดทันที
    const handlePlayerAdded = (data = {}) => {
      if (!isCurrentGroupEvent(data)) return;

      console.log("👤 PLAYER ADDED, RELOAD PLAYERS:", data);

      // แสดงสมาชิกใหม่ทันทีจาก Socket ก่อน แล้วค่อยโหลดข้อมูลจริงจาก DB
      // เพื่อยืนยันค่าอีกครั้ง จึงไม่ต้องรอ fetch ก่อนชื่อจะปรากฏบนหน้าจอ
      if (Number.isInteger(Number(data.user_id)) && data.name) {
        setAllPlayers(currentPlayers => {
          const existingPlayer = currentPlayers.find(
            player => Number(player.user_id) === Number(data.user_id)
          );
          const incomingStatus =
            getMemberStatus(data) ||
            getMemberStatus(existingPlayer) ||
            'ACTIVE';
          const incomingPlayer = {
            ...existingPlayer,
            ...data,
            user_id: Number(data.user_id),
            point: Number(data.point ?? existingPlayer?.point ?? 0),
            status: incomingStatus,
            memberStatus: incomingStatus,
            checkin_time:
              data.checkin_time ||
              existingPlayer?.checkin_time ||
              new Date().toISOString(),
            active_since:
              data.active_since ||
              existingPlayer?.active_since ||
              new Date().toISOString(),
            totalMatches: Number(
              data.totalMatches ??
              existingPlayer?.totalMatches ??
              existingPlayer?.total_matches ??
              0
            )
          };
          const withoutIncoming = currentPlayers.filter(
            player => Number(player.user_id) !== incomingPlayer.user_id
          );

          return [incomingPlayer, ...withoutIncoming];
        });
      }

      loadPlayers().catch(error => {
        console.error("LOAD PLAYERS AFTER PLAYER ADDED ERROR:", error);
      });
    };

    const handleRosterUpdated = (data = {}) => {
      if (!isCurrentGroupEvent(data)) return;
      Promise.all([loadPlayers(), loadQueue(), loadMatches()]).catch(error => {
        console.error("RELOAD ROSTER AFTER SOCKET EVENT ERROR:", error);
      });
    };

    const handleCourtsUpdated = (data = {}) => {
      if (!isCurrentGroupEvent(data)) return;
      Promise.all([loadCourts(), loadMatches()]).catch(error => {
        console.error("RELOAD COURTS AFTER SOCKET EVENT ERROR:", error);
      });
    };

    const handleLockedPairsUpdated = (data = {}) => {
      if (!isCurrentGroupEvent(data)) return;
      loadLockedPairs().catch(error => {
        console.error("RELOAD LOCKED PAIRS AFTER SOCKET EVENT ERROR:", error);
      });
    };

    socket.on("match-updated", handleMatchUpdated);
    socket.on("queue-updated", handleQueueUpdated);
    socket.on("match-started", handleMatchStarted);
    socket.on("match-ended", handleMatchEnded);
    socket.on("match-generated", handleQueueUpdated);
    socket.on("player-added", handlePlayerAdded);
    socket.on("member-joined", handlePlayerAdded);
    socket.on("member-updated", handlePlayerAdded);
    socket.on("player-style-updated", handlePlayerAdded);
    socket.on("player-status-updated", handlePlayerStatusUpdated);
    socket.on("member-checkout", handleRosterUpdated);
    socket.on("player-updated", handleRosterUpdated);
    socket.on("point-updated", handleRosterUpdated);
    socket.on("courts-added", handleCourtsUpdated);
    socket.on("court-status-updated", handleCourtsUpdated);
    socket.on("court-deleted", handleCourtsUpdated);
    socket.on("courts-initialized", handleCourtsUpdated);
    socket.on("locked-pairs-updated", handleLockedPairsUpdated);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("match-updated", handleMatchUpdated);
      socket.off("queue-updated", handleQueueUpdated);
      socket.off("match-started", handleMatchStarted);
      socket.off("match-ended", handleMatchEnded);
      socket.off("match-generated", handleQueueUpdated);
      socket.off("player-added", handlePlayerAdded);
      socket.off("member-joined", handlePlayerAdded);
      socket.off("member-updated", handlePlayerAdded);
      socket.off("player-style-updated", handlePlayerAdded);
      socket.off("player-status-updated", handlePlayerStatusUpdated);
      socket.off("member-checkout", handleRosterUpdated);
      socket.off("player-updated", handleRosterUpdated);
      socket.off("point-updated", handleRosterUpdated);
      socket.off("courts-added", handleCourtsUpdated);
      socket.off("court-status-updated", handleCourtsUpdated);
      socket.off("court-deleted", handleCourtsUpdated);
      socket.off("courts-initialized", handleCourtsUpdated);
      socket.off("locked-pairs-updated", handleLockedPairsUpdated);
    };

  }, [
    groupId,
    loadCourts,
    loadLockedPairs,
    loadMatches,
    loadPlayers,
    loadQueue
  ]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setOverviewNow(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setPlayingNow(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  // รายชื่อ/สถานะเป็นข้อมูลที่ Alert ใช้โดยตรง จึงซิงก์ถี่กว่าข้อมูลทั้งบอร์ด
  // เพื่อให้ผู้เล่นที่ถูกจัดเข้า Queue, PLAY หรือจบ Match หายจากรายการทันที
  useEffect(() => {
    if (!groupId) return undefined;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      loadPlayers().catch(error => {
        console.error('BACKGROUND PLAYER ALERT SYNC ERROR:', error);
      });
    }, 15000);

    return () => window.clearInterval(intervalId);
  }, [groupId, loadPlayers]);

  // Socket เป็นช่องทางหลัก ส่วน polling เป็น safety net เมื่อ browser
  // หลุดจาก room ชั่วคราว หรือ Backend เก่าส่ง event คนละชื่อ
  useEffect(() => {
    if (!groupId) return undefined;

    const syncBoard = () => {
      const request = Promise.all([
        loadPlayers(),
        loadQueue(),
        loadMatches(),
        loadCourts(),
        loadLockedPairs()
      ]);

      Promise.resolve(request).catch(error => {
        console.error("BACKGROUND BOARD SYNC ERROR:", error);
      });
    };
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') syncBoard();
    }, 30000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncBoard();
    };
    const handleFocus = () => syncBoard();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
      window.removeEventListener('focus', handleFocus);
    };
  }, [
    groupId,
    isMonitorBoard,
    loadCourts,
    loadLockedPairs,
    loadMatches,
    loadPlayers,
    loadQueue
  ]);

  const reloadAll = useCallback(() =>
    Promise.all([
      loadQueue(),
      loadMatches(),
      loadCourts(),
      loadLockedPairs(),
      loadPlayers()
    ]), [
      loadCourts,
      loadLockedPairs,
      loadMatches,
      loadPlayers,
      loadQueue
    ]);

  useEffect(() => {
    if (!groupId) return;
    reloadAll().catch(error => {
      console.error('INITIAL MATCH LOAD ERROR:', error);
    });
  }, [groupId, reloadAll]);

  /* =========================
     UTIL
  ========================== */

  const sumPoints = players =>
    players.reduce((s, p) => s + (p.point || 0), 0);

  const getPairCount = (a, b) => {
    const key1 = `${a.user_id}-${b.user_id}`;
    const key2 = `${b.user_id}-${a.user_id}`;
    return pairMap[key1] || pairMap[key2] || 0;
  }; const getOpponentCount = (a, b) => {
    const key1 = `${a.user_id}-${b.user_id}`;
    const key2 = `${b.user_id}-${a.user_id}`;
    return opponentMap[key1] || opponentMap[key2] || 0;
  };
  /* =========================
     LOCK PAIR
  ========================== */

  const lockPair = async () => {
    if (!lockA || !lockB) {
      console.warn('LOCK PAIR: กรุณาเลือกผู้เล่นให้ครบ 2 คน');
      return;
    }

    if (Number(lockA.user_id) === Number(lockB.user_id)) {
      console.warn('LOCK PAIR: กรุณาเลือกผู้เล่นคนละคน');
      return;
    }

    await api('/api/game-match/lock-pair', 'POST', {
      groupId,
      userA: Number(lockA.user_id),
      userB: Number(lockB.user_id)
    });

    setLockA(null);
    setLockB(null);
    await loadLockedPairs();
  };

  const removeLock = async (a, b) => {
    await api('/api/game-match/lock-pair', 'DELETE', {
      groupId,
      userA: a,
      userB: b
    });
    loadLockedPairs();
  };

  const cancelQueue = async (matchId) => {
    if (!window.confirm('ยืนยันยกเลิก Queue นี้? ผู้เล่นที่ไม่มี Match อื่นจะกลับเป็น ACTIVE')) {
      return;
    }

    try {
      await api(`/api/game-match/queue/${matchId}`, 'DELETE');
      await reloadAll();
    } catch (error) {
      alert(`❌ ยกเลิก Queue ไม่สำเร็จ: ${error.message}`);
    }
  };

  const cancelPlayingMatch = async match => {
    if (!window.confirm(
      `ยืนยันยกเลิก Match #${getMatchDisplayNo(match)}? ผู้เล่นและ Court จะถูกคืนสถานะ`
    )) {
      return;
    }

    try {
      await api(`/api/game-match/playing/${match.matchId}`, 'DELETE', {
        groupId
      });
      setEditingMatchId(null);
      await reloadAll();
    } catch (error) {
      alert(`❌ ยกเลิก Match ไม่สำเร็จ: ${error.message}`);
    }
  };
  void cancelPlayingMatch;

  const endSelectedPlayingMatches = async () => {
    if (selectedPlayingMatches.length === 0 || bulkEndingMatches) return;

    setBulkEndingMatches(true);
    setBulkEndMessage(null);

    const failedMatches = [];

    for (const match of selectedPlayingMatches) {
      try {
        await api('/api/game-match/end', 'POST', {
          matchId: match.matchId,
          winnerTeam: 'DRAW'
        });
      } catch (error) {
        failedMatches.push({
          matchId: String(match.matchId),
          matchNo: getMatchDisplayNo(match),
          message: error.message
        });
      }
    }

    await reloadAll().catch(error => {
      console.error('RELOAD AFTER END ALL ERROR:', error);
    });

    if (failedMatches.length === 0) {
      setBulkEndMessage({
        type: 'success',
        text: `จบการแข่งขันเป็น DRAW สำเร็จ ${selectedPlayingMatches.length} Match`
      });
      setSelectedEndMatchIds([]);
    } else {
      const failedNumbers = failedMatches
        .map(item => `#${item.matchNo}`)
        .join(', ');

      setBulkEndMessage({
        type: 'error',
        text: `จบสำเร็จ ${selectedPlayingMatches.length - failedMatches.length}/${selectedPlayingMatches.length} Match — ไม่สำเร็จ ${failedNumbers}`
      });
      setSelectedEndMatchIds(failedMatches.map(item => item.matchId));
    }

    setBulkEndingMatches(false);
  };

  /* =========================
     START MATCH
  ========================== */

  const announceStartedMatch = (
    match,
    startData,
    { clearPending = false } = {}
  ) => {
    if (!matchVoiceEnabled) return false;

    return enqueueGoogleMatchAnnouncement(
      match,
      startData,
      {
        clearPending,
        voiceName: googleTtsVoice
      }
    );
  };

  const testMatchAnnouncement = () => {
    unlockAnnouncementAudio();

    const sampleMatch = queue[0] || {
      groupMatchNo: 1,
      team1: [{ name: 'เอ' }, { name: 'บี' }],
      team2: [{ name: 'ซี' }, { name: 'ดี' }]
    };
    const supported = enqueueGoogleMatchAnnouncement(
      sampleMatch,
      {
        groupMatchNo: getMatchDisplayNo(sampleMatch),
        courtNo: 9
      },
      {
        clearPending: true,
        voiceName: googleTtsVoice
      }
    );

    if (!supported) {
      console.warn(
        'เบราว์เซอร์นี้ไม่รองรับเสียงประกาศ กรุณาใช้ Chrome หรือ Edge รุ่นปัจจุบัน'
      );
    }
  };

  const recallPlayingMatch = match => {
    unlockAnnouncementAudio();

    const matchPlayers = Array.isArray(match?.players) ? match.players : [];
    const voiceMatch = {
      ...match,
      team1: Array.isArray(match?.team1)
        ? match.team1
        : matchPlayers.filter(player => Number(player.team) === 1),
      team2: Array.isArray(match?.team2)
        ? match.team2
        : matchPlayers.filter(player => Number(player.team) === 2)
    };
    const supported = enqueueGoogleMatchAnnouncement(
      voiceMatch,
      {
        groupMatchNo: getMatchDisplayNo(match),
        courtNo: match?.court_no || match?.courtNo || '-'
      },
      {
        clearPending: true,
        voiceName: googleTtsVoice
      }
    );

    if (!supported) {
      console.warn('เบราว์เซอร์นี้ไม่รองรับเสียงเรียก กรุณาใช้ Chrome หรือ Edge รุ่นปัจจุบัน');
    }
  };
  void recallPlayingMatch;

  const requestStartMatch = (match, approveStatus = false) => api('/api/game-match/start', 'POST', {
    matchId: match.matchId,
    groupId,
    approvePlaying: approveStatus
  });

  const getHardQueueBlockers = match => (
    getQueueBlockers(match).filter(
      player => getMemberStatus(player) === 'HOLD'
    )
  );

  const getQueueApprovalBlockers = match => (
    getQueueBlockers(match).filter(
      player => getMemberStatus(player) !== 'HOLD'
    )
  );

  const requestStartMatchWithApproval = async match => {
    try {
      return await requestStartMatch(match, false);
    } catch (error) {
      const requiresApproval = Boolean(
        error.data?.requiresPlayingApproval ||
        error.data?.requiresStatusApproval
      );

      if (!requiresApproval) throw error;

      const responsePlayers = Array.isArray(error.data?.approvalPlayers)
        ? error.data.approvalPlayers
        : [];
      const queuePlayers = getQueueApprovalBlockers(match);
      const approvalPlayers = responsePlayers.length > 0
        ? responsePlayers
        : queuePlayers;
      const uniquePlayers = approvalPlayers.filter((player, index, players) =>
        players.findIndex(item =>
          Number(item.user_id) === Number(player.user_id)
        ) === index
      );
      const playerLines = uniquePlayers.length > 0
        ? uniquePlayers.map(player =>
          `• ${player.name} (${getMemberStatus(player) || player.status || 'ไม่ทราบสถานะ'})`
        ).join('\n')
        : '• มีผู้เล่นที่ยังไม่ ACTIVE';
      const playingMatchLines = Array.isArray(error.data?.playingConflicts)
        ? error.data.playingConflicts.map(conflict =>
          `• Match #${conflict.groupMatchNo || conflict.matchId}` +
          `${conflict.courtNo ? ` • Court ${conflict.courtNo}` : ''}`
        ).join('\n')
        : '';
      const confirmed = window.confirm(
        `ผู้เล่นยังไม่อยู่สถานะ ACTIVE ก่อน Start Match #${getMatchDisplayNo(match)}\n\n` +
        `${playerLines}` +
        `${playingMatchLines ? `\n\nกำลังเล่นอยู่ใน:\n${playingMatchLines}` : ''}` +
        '\n\nต้องการ Approve อีกครั้งและ Start Match นี้หรือไม่?'
      );

      if (!confirmed) return null;
      return requestStartMatch(match, true);
    }
  };

  const startMatch = async match => {
    if (startingId || bulkQueueAction) return;

    if (matchVoiceEnabled) {
      unlockAnnouncementAudio();
    }

    const hardBlockers = getHardQueueBlockers(match);

    if (hardBlockers.length > 0) {
      const names = hardBlockers.map(player => (
        `${player.name} (${getMemberStatus(player) || '-'})`
      )).join(', ');
      console.warn(
        `START MATCH BLOCKED: ${names} อยู่สถานะ HOLD`
      );
      return;
    }

    try {
      setStartingId(match.matchId);
      const data = await requestStartMatchWithApproval(match);

      if (!data) return;

      announceStartedMatch(
        match,
        data,
        { clearPending: true }
      );

      await reloadAll();
    } catch (error) {
      console.error('START MATCH ERROR:', error);
      await reloadAll().catch(() => {});
    } finally {
      setStartingId(null);
    }
  };
/* =================================================
   SWAP TEAM - PLAYING MATCH
================================================= */

const swapPlayingTeams = async match => {
  if (!match?.matchId) return;

  const team1 = match.players.filter(
    player => Number(player.team) === 1
  );

  const team2 = match.players.filter(
    player => Number(player.team) === 2
  );

  if (
    team1.length !== 2 ||
    team2.length !== 2
  ) {
    alert('ข้อมูลผู้เล่นใน Match ไม่ครบ 2 ต่อ 2');
    return;
  }

  const confirmed = window.confirm(
    `ต้องการสลับ Team 1 ↔ Team 2 ของ Match #${match.matchId} หรือไม่?`
  );

  if (!confirmed) return;

  try {

    const response = await fetch(
      `${API_BASE}/api/game-match/update-players-playing`,
      {
        method: 'POST',

        headers: {
          'Content-Type': 'application/json'
        },

        body: JSON.stringify({
          matchId: match.matchId,
          groupId,

          // สลับทีม
          team1: team2,
          team2: team1
        })
      }
    );

    const data = await response
      .json()
      .catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data.message ||
        'สลับทีมไม่สำเร็จ'
      );
    }

    await loadMatches();

  } catch (error) {

    console.error(
      '[SWAP PLAYING TEAM ERROR]',
      error
    );

    alert(
      error.message ||
      'สลับทีมไม่สำเร็จ'
    );
  }
};
  const startAllQueues = async () => {
    if (queue.length === 0 || bulkQueueAction || startingId) return;

    if (matchVoiceEnabled) {
      unlockAnnouncementAudio();
    }

    const openCourtCount = courts.filter(
      court => String(court.status || '').toUpperCase() === 'OPEN'
    ).length;

    if (openCourtCount === 0) {
      console.warn('START ALL: ไม่มีคอร์ทว่างสำหรับ Start Queue');
      return;
    }

    setBulkQueueAction('start');

    const started = [];
    const failed = [];
    let stoppedBecauseNoCourt = false;

    try {
      for (const match of [...queue]) {
        const hardBlockers = getHardQueueBlockers(match);

        if (hardBlockers.length > 0) {
          failed.push({
            matchId: match.matchId,
            groupMatchNo: getMatchDisplayNo(match),
            reason: hardBlockers
              .map(player => `${player.name} (${getMemberStatus(player) || '-'})`)
              .join(', ')
          });
          continue;
        }

        setStartingId(match.matchId);

        try {
          const data = await requestStartMatchWithApproval(match);

          if (!data) {
            failed.push({
              matchId: match.matchId,
              groupMatchNo: getMatchDisplayNo(match),
              reason: 'ยกเลิกการ Approve ก่อน Start Match'
            });
            continue;
          }

          started.push({
            matchId: match.matchId,
            groupMatchNo: getMatchDisplayNo(match),
            courtNo: data.courtNo,
            match
          });
        } catch (error) {
          if (/no court available|ไม่มีคอร์ทว่าง/i.test(error.message || '')) {
            stoppedBecauseNoCourt = true;
            break;
          }

          failed.push({
            matchId: match.matchId,
            groupMatchNo: getMatchDisplayNo(match),
            reason: error.message
          });
        }
      }

      await reloadAll();

      started.forEach((item, index) => {
        announceStartedMatch(
          item.match,
          {
            groupMatchNo: item.groupMatchNo,
            courtNo: item.courtNo
          },
          { clearPending: index === 0 }
        );
      });

      if (failed.length > 0 || stoppedBecauseNoCourt) {
        console.warn('START ALL PARTIAL RESULT:', {
          started: started.length,
          failed,
          stoppedBecauseNoCourt
        });
      }
    } catch (error) {
      console.error('START ALL ERROR:', error);
      await reloadAll().catch(() => {});
    } finally {
      setStartingId(null);
      setBulkQueueAction(null);
    }
  };

  const cancelAllQueues = async () => {
    if (queue.length === 0 || bulkQueueAction || startingId) return;

    if (!window.confirm(
      `ยืนยัน Cancel All จำนวน ${queue.length} Queue?\n\n` +
      'ผู้เล่นที่ไม่มี Queue หรือ Match อื่นจะกลับเป็น ACTIVE'
    )) {
      return;
    }

    setBulkQueueAction('cancel');

    try {
      await api(
        `/api/game-match/queue/all/${groupId}`,
        'DELETE'
      );
      setEditingQueueId(null);
      await reloadAll();
    } catch (error) {
      alert(`❌ Cancel All ไม่สำเร็จ: ${error.message}`);
    } finally {
      setBulkQueueAction(null);
    }
  };

  /* =========================
     EDIT QUEUE
  ========================== */

  const startEditQueue = m => {
    setEditingQueueId(m.matchId);
    setEditQueueTeams({
      team1: [...m.team1],
      team2: [...m.team2]
    });
  };

  const saveQueueEdit = async () => {
    try {
      await api('/api/game-match/update-players', 'POST', {
        matchId: editingQueueId,
        team1: editQueueTeams.team1,
        team2: editQueueTeams.team2
      });

      setEditingQueueId(null);
      await reloadAll();
    } catch (error) {
      alert(`❌ แก้ไข Queue ไม่สำเร็จ: ${error.message}`);
    }

  };

  const swapMatchPartners = async (match, matchType) => {
    const isPlayingMatch = matchType === 'playing';
    const sourceTeam1 = isPlayingMatch
      ? match.players.filter(player => Number(player.team) === 1)
      : match.team1;
    const sourceTeam2 = isPlayingMatch
      ? match.players.filter(player => Number(player.team) === 2)
      : match.team2;

    if (sourceTeam1.length !== 2 || sourceTeam2.length !== 2) {
      console.error(
        'SWAP PARTNERS ERROR: Team 1 and Team 2 must contain 2 players each'
      );
      return;
    }

    const matchNo = getMatchDisplayNo(match);
    const actionKey = `${matchType}-${match.matchId}`;

    if (swappingPairKey) return;

    /*
      Build the three possible partner arrangements from the same four
      players. The current arrangement is compared without using team side
      or display order, so the cycle still works after refresh/socket reload.
    */
    const sortedPlayers = [...sourceTeam1, ...sourceTeam2].sort(
      (left, right) => Number(left.user_id) - Number(right.user_id)
    );
    const [player1, player2, player3, player4] = sortedPlayers;
    const layouts = [
      { team1: [player1, player2], team2: [player3, player4] },
      { team1: [player1, player3], team2: [player2, player4] },
      { team1: [player1, player4], team2: [player2, player3] }
    ];
    const pairKey = team =>
      team
        .map(player => Number(player.user_id))
        .sort((left, right) => left - right)
        .join('-');
    const layoutKey = (team1, team2) =>
      [pairKey(team1), pairKey(team2)].sort().join('|');
    const currentLayoutKey = layoutKey(sourceTeam1, sourceTeam2);
    const currentLayoutIndex = layouts.findIndex(
      layout => layoutKey(layout.team1, layout.team2) === currentLayoutKey
    );
    const nextLayoutIndex =
      currentLayoutIndex >= 0
        ? (currentLayoutIndex + 1) % layouts.length
        : 0;
    const nextTeam1 = layouts[nextLayoutIndex].team1;
    const nextTeam2 = layouts[nextLayoutIndex].team2;

    setSwappingPairKey(actionKey);

    try {
      await api(
        isPlayingMatch
          ? '/api/game-match/update-players-playing'
          : '/api/game-match/update-players',
        'POST',
        {
          matchId: match.matchId,
          ...(isPlayingMatch ? { groupId } : {}),
          team1: nextTeam1,
          team2: nextTeam2
        }
      );
      await reloadAll();
    } catch (error) {
      console.error(`SWAP PARTNERS MATCH #${matchNo} ERROR:`, error);
    } finally {
      setSwappingPairKey(null);
    }
  };

  /* =========================
     EDIT PLAYING
  ========================== */

  const startEditPlayingMatch = m => {
    setEditingMatchId(m.matchId);
    setEditTeams({
      team1: m.players.filter(p => p.team === 1),
      team2: m.players.filter(p => p.team === 2)
    });
  };

  const savePlayingPlayers = async () => {
    try {
      await api('/api/game-match/update-players-playing', 'POST', {
        matchId: editingMatchId,
        groupId,
        team1: editTeams.team1,
        team2: editTeams.team2
      });

      setEditingMatchId(null);
      await reloadAll();
    } catch (error) {
      alert(`❌ แก้ไข Match Start ไม่สำเร็จ: ${error.message}`);
    }


  };

  const openMoveCourt = match => {
    setMovingCourtMatch(match);
    setTargetCourtNo(null);
  };

  const closeMoveCourt = () => {
    if (movingCourt) return;
    setMovingCourtMatch(null);
    setTargetCourtNo(null);
  };

  const submitMoveCourt = async () => {
    if (!movingCourtMatch || !targetCourtNo || movingCourt) return;

    const currentCourtNo = Number(movingCourtMatch.court_no);
    const selectedCourtNo = Number(targetCourtNo);

    if (currentCourtNo === selectedCourtNo) {
      console.warn(
        `MOVE COURT: Match #${getMatchDisplayNo(movingCourtMatch)} อยู่ Court ${selectedCourtNo} แล้ว`
      );
      return;
    }

    setMovingCourt(true);

    try {
      await api('/api/game-match/move-court', 'POST', {
        matchId: movingCourtMatch.matchId,
        groupId,
        targetCourtNo: selectedCourtNo,
        // ถ้าคอร์ทไม่ว่าง ให้สลับคอร์ททันทีโดยไม่เปิด Confirm ซ้ำ
        approveSwap: true
      });
      setMovingCourtMatch(null);
      setTargetCourtNo(null);
      await reloadAll();
    } catch (error) {
      console.error('MOVE COURT ERROR:', error);
      await reloadAll().catch(() => {});
    } finally {
      setMovingCourt(false);
    }
  };

  const getIdleMinutes = (lastFinishedAt) => {
    if (!lastFinishedAt) return 999;

    const diff = Date.now() - new Date(lastFinishedAt).getTime();
    return Math.floor(diff / 60000);
  };
  /* =========================
     RENDER
  ========================== */

  return (
    <div
      className={`gm-container ${
        isMonitorBoard ? 'gm-monitor-board-mode' : 'gm-dashboard-mode'
      }`}
    >

      {!isMonitorBoard && (
        <>
          <h2 className="gm-main-title">🎲 Game Match Control</h2>

          <div className="gm-overview-visibility-row">
            <button
              type="button"
              className="gm-overview-toggle-button"
              onClick={() => setMemberOverviewVisible(visible => !visible)}
            >
              {memberOverviewVisible
                ? '🙈 ซ่อนข้อมูลภาพรวมสมาชิก'
                : '👀 แสดงข้อมูลภาพรวมสมาชิก'}
            </button>
          </div>

          {/* MEMBER OVERVIEW */}
          {memberOverviewVisible && (
          <div className="gm-overview-card">
        <div className="gm-overview-grid">

          <section className="gm-overview-column gm-games-column">
            <div className="gm-overview-heading gm-games-heading">
              <div>
                <span className="gm-overview-icon">🎮</span>
                <strong>จำนวนเกมที่ลงเล่น</strong>
              </div>
              <span className="gm-overview-count">
                สมาชิกทั้งหมด {allPlayers.length} คน
              </span>
            </div>

            <div className="gm-games-controls">
              <label className="gm-games-search">
                <span aria-hidden="true">🔎</span>
                <input
                  type="search"
                  value={gamesMonitorKeyword}
                  placeholder="ค้นหาชื่อสมาชิก"
                  onChange={event => {
                    setGamesMonitorKeyword(event.target.value);
                    setGamesMonitorPage(1);
                  }}
                />
                {gamesMonitorKeyword && (
                  <button
                    type="button"
                    aria-label="ล้างคำค้นหาสมาชิก"
                    onClick={() => {
                      setGamesMonitorKeyword('');
                      setGamesMonitorPage(1);
                    }}
                  >
                    ×
                  </button>
                )}
              </label>
              <span className="gm-games-range">
                {gamesMonitorPlayers.length === 0
                  ? 'ไม่พบสมาชิก'
                  : `แสดง ${gamesMonitorStartIndex + 1}-${
                    gamesMonitorStartIndex + gamesMonitorPagePlayers.length
                  } จาก ${gamesMonitorPlayers.length} คน`}
              </span>
            </div>

            <div className="gm-games-monitor-list">
              {gamesMonitorPagePlayers.length === 0 && (
                <div className="gm-overview-empty gm-games-empty">
                  {gamesMonitorKeyword.trim()
                    ? 'ไม่พบชื่อสมาชิกที่ค้นหา'
                    : 'ยังไม่มีข้อมูลสมาชิก'}
                </div>
              )}

              {gamesMonitorPagePlayers.map((player, index) => (
                <div
                  key={player.user_id}
                  className="gm-compact-row gm-member-row gm-games-member-row"
                >
                  <span className="gm-member-order gm-games-order">
                    {gamesMonitorStartIndex + index + 1}
                  </span>
                  <span className="gm-member-info">
                    <strong>{player.name}</strong>
                    <small>
                      มือ {getHandLevel(player)} •{' '}
                      {getMemberStatus(player) || '-'}
                    </small>
                  </span>
                  <span className="gm-member-time gm-games-count">
                    {getTotalMatches(player)} เกม
                  </span>
                </div>
              ))}
            </div>

            <div className="gm-games-pagination">
              <button
                type="button"
                disabled={gamesMonitorPage <= 1}
                onClick={() => setGamesMonitorPage(page => Math.max(1, page - 1))}
              >
                ‹ ก่อนหน้า
              </button>
              <label>
                <span>หน้า</span>
                <select
                  value={gamesMonitorPage}
                  onChange={event => setGamesMonitorPage(Number(event.target.value))}
                  aria-label="เลือกหน้ารายชื่อจำนวนเกม"
                >
                  {Array.from(
                    { length: gamesMonitorTotalPages },
                    (_, pageIndex) => pageIndex + 1
                  ).map(pageNumber => (
                    <option key={pageNumber} value={pageNumber}>
                      {pageNumber}
                    </option>
                  ))}
                </select>
                <span>/ {gamesMonitorTotalPages}</span>
              </label>
              <button
                type="button"
                disabled={gamesMonitorPage >= gamesMonitorTotalPages}
                onClick={() => setGamesMonitorPage(page =>
                  Math.min(gamesMonitorTotalPages, page + 1)
                )}
              >
                ถัดไป ›
              </button>
            </div>
          </section>

          <section className="gm-overview-column gm-lock-column">
            <div className="gm-overview-heading">
              <div>
                <span className="gm-overview-icon">🔒</span>
                <strong>Locked Pairs</strong>
              </div>
              <span className="gm-overview-count">
                {lockedPairs.length} คู่
              </span>
            </div>

            <div className="gm-compact-list gm-lock-list">
              {lockedPairs.length === 0 && (
                <div className="gm-overview-empty">
                  ยังไม่มีคู่ล็อก
                </div>
              )}

              {lockedPairs.map((pair, index) => (
                <div
                  key={`${pair.user_a}-${pair.user_b}-${index}`}
                  className="gm-compact-row gm-lock-row"
                >
                  <span className="gm-lock-names">
                    {pair.name_a}
                    <b>+</b>
                    {pair.name_b}
                  </span>
                  <button
                    type="button"
                    className="gm-icon-remove"
                    title="ลบคู่ล็อก"
                    aria-label={`ลบคู่ล็อก ${pair.name_a} และ ${pair.name_b}`}
                    onClick={() => removeLock(pair.user_a, pair.user_b)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="gm-compact-lock-form">
              <PlayerSearchDropdown
                value={lockA}
                allPlayers={allPlayers}
                usedIds={[lockB?.user_id].filter(Boolean)}
                onChange={setLockA}
              />

              <PlayerSearchDropdown
                value={lockB}
                allPlayers={allPlayers}
                usedIds={[lockA?.user_id].filter(Boolean)}
                onChange={setLockB}
              />

              <button
                type="button"
                className="gm-compact-lock-button"
                onClick={lockPair}
                disabled={!lockA || !lockB}
              >
                🔒 Lock
              </button>
            </div>
          </section>

          <section className="gm-overview-column gm-new-column">
            <div className="gm-overview-heading">
              <div>
                <span className="gm-overview-icon">✨</span>
                <strong>สมาชิกมาใหม่</strong>
              </div>
              <span className="gm-overview-count">
                ล่าสุด {recentMembers.length} คน
              </span>
            </div>

            <div className="gm-compact-list">
              {recentMembers.length === 0 && (
                <div className="gm-overview-empty">
                  ยังไม่มีสมาชิก
                </div>
              )}

              {recentMembers.map((player, index) => (
                <div
                  key={player.user_id}
                  className="gm-compact-row gm-member-row"
                >
                  <span className="gm-member-order">
                    {index + 1}
                  </span>
                  <span className="gm-member-info">
                    <strong>{player.name}</strong>
                    <small>
                      มือ {getHandLevel(player)} •{' '}
                      {getDailyPlayStyleLabel(player)}
                    </small>
                  </span>
                  <span className="gm-member-time gm-new-time">
                    {formatElapsedTime(
                      getMemberArrivalTime(player),
                      overviewNow
                    )}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className="gm-overview-column gm-wait-column">
            <div className="gm-overview-heading">
              <div>
                <span className="gm-overview-icon">⏱</span>
                <strong>สมาชิกที่รอนาน</strong>
              </div>
              <span className="gm-overview-count">
                ACTIVE {activeMemberCount} คน
              </span>
            </div>

            <div className="gm-compact-list">
              {longestWaitingMembers.length === 0 && (
                <div className="gm-overview-empty">
                  ไม่มีสมาชิกที่กำลังรอ
                </div>
              )}

              {longestWaitingMembers.map((player, index) => (
                <div
                  key={player.user_id}
                  className="gm-compact-row gm-member-row"
                >
                  <span className="gm-member-order gm-wait-order">
                    {index + 1}
                  </span>
                  <span className="gm-member-info">
                    <strong>{player.name}</strong>
                    <small>
                      มือ {getHandLevel(player)} •{' '}
                      {getDailyPlayStyleLabel(player)}
                    </small>
                  </span>
                  <span className="gm-member-time gm-wait-time">
                    {formatElapsedTime(
                      getMemberWaitingTime(player),
                      overviewNow
                    )}
                  </span>
                </div>
              ))}
            </div>
          </section>

        </div>

        <div className="gm-member-summary">
          <div className="gm-total-members">
            <span>👥 สมาชิกทั้งหมด</span>
            <strong>{allPlayers.length} คน</strong>
          </div>

          <div className="gm-hand-summary">
            <span className="gm-summary-label">ระดับมือทั้งหมด</span>
            <div className="gm-hand-chips">
              {handLevelSummary.map(item => (
                <span
                  key={item.level}
                  className={`gm-hand-chip ${
                    item.count === 0 ? 'is-empty' : ''
                  }`}
                >
                  <b>{item.level}</b>
                  <span>{item.count}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
          </div>
          )}

          <div className={`gm-floating-member-dock ${floatingMemberMenuOpen ? 'is-open' : 'is-closed'}`}>
            <button type="button" className="gm-floating-member-toggle" aria-expanded={floatingMemberMenuOpen} onClick={() => setFloatingMemberMenuOpen(open => !open)}>
              {floatingMemberMenuOpen ? '✕ ปิดเมนู' : '☰ เมนูข้อมูล'}
            </button>
            {floatingMemberMenuOpen && <div className="gm-floating-member-menu" aria-label="เมนูข้อมูลสมาชิกลอย">
              <button type="button" onClick={() => setFloatingMemberModal('GAMES')}>🎮 จำนวนที่ลงเล่น</button>
              <button type="button" onClick={() => setFloatingMemberModal('RECENT')}>✨ สมาชิกมาใหม่</button>
              <button type="button" onClick={() => setFloatingMemberModal('WAITING')}>⏱ ดูคนรอนาน <b>{longestWaitingMembers.length}</b></button>
              <button type="button" onClick={() => setFloatingMemberModal('LOCKS')}>🔒 Locked Pairs <b>{lockedPairs.length}</b></button>
            </div>}
          </div>

          {floatingMemberModal && (
            <div
              className="gm-floating-modal-backdrop"
              role="presentation"
              onMouseDown={event => {
                if (event.target === event.currentTarget) setFloatingMemberModal(null);
              }}
            >
              <section className="gm-floating-member-modal" role="dialog" aria-modal="true">
                <div className="gm-floating-modal-heading">
                  <strong>
                    {floatingMemberModal === 'GAMES' && '🎮 จำนวนเกมที่ลงเล่น'}
                    {floatingMemberModal === 'RECENT' && '✨ สมาชิกมาใหม่'}
                    {floatingMemberModal === 'LOCKS' && '🔒 Locked Pairs'}
                    {floatingMemberModal === 'WAITING' && '⏱ สถานะและเวลารอผู้เล่น'}
                  </strong>
                  <button type="button" onClick={() => setFloatingMemberModal(null)}>
                    ✕ ปิด
                  </button>
                </div>

                {floatingMemberModal === 'GAMES' && (
                  <>
                    <label className="gm-floating-search">
                      <span>🔎</span>
                      <input
                        type="search"
                        value={gamesMonitorKeyword}
                        placeholder="ค้นหาชื่อสมาชิก"
                        onChange={event => {
                          setGamesMonitorKeyword(event.target.value);
                          setGamesMonitorPage(1);
                        }}
                      />
                    </label>
                    <div className="gm-floating-list">
                      {gamesMonitorPagePlayers.map((player, index) => (
                        <div key={player.user_id} className="gm-floating-member-row">
                          <b>{gamesMonitorStartIndex + index + 1}</b>
                          <span><strong>{player.name}</strong><small>Rank {getHandLevel(player)}</small></span>
                          <em>{getTotalMatches(player)} เกม</em>
                        </div>
                      ))}
                      {gamesMonitorPagePlayers.length === 0 && <p>ไม่พบสมาชิก</p>}
                    </div>
                    <div className="gm-floating-pagination">
                      <button type="button" disabled={gamesMonitorPage <= 1} onClick={() => setGamesMonitorPage(page => Math.max(1, page - 1))}>‹ ก่อนหน้า</button>
                      <span>{gamesMonitorPage}/{gamesMonitorTotalPages}</span>
                      <button type="button" disabled={gamesMonitorPage >= gamesMonitorTotalPages} onClick={() => setGamesMonitorPage(page => Math.min(gamesMonitorTotalPages, page + 1))}>ถัดไป ›</button>
                    </div>
                  </>
                )}

                {floatingMemberModal === 'RECENT' && (
                  <div className="gm-floating-list">
                    {recentMembers.map((player, index) => (
                      <div key={player.user_id} className="gm-floating-member-row">
                        <b>{index + 1}</b>
                        <span><strong>{player.name}</strong><small>Rank {getHandLevel(player)} • {getDailyPlayStyleLabel(player)}</small></span>
                        <em>{formatElapsedTime(getMemberArrivalTime(player), overviewNow)}</em>
                      </div>
                    ))}
                    {recentMembers.length === 0 && <p>ยังไม่มีสมาชิกมาใหม่</p>}
                  </div>
                )}

                {floatingMemberModal === 'LOCKS' && (
                  <>
                    <div className="gm-floating-list">
                      {lockedPairs.map((pair, index) => (
                        <div key={`${pair.user_a}-${pair.user_b}-${index}`} className="gm-floating-lock-row">
                          <span>{pair.name_a} <b>+</b> {pair.name_b}</span>
                          <button type="button" onClick={() => removeLock(pair.user_a, pair.user_b)}>ลบ</button>
                        </div>
                      ))}
                      {lockedPairs.length === 0 && <p>ยังไม่มีคู่ล็อก</p>}
                    </div>
                    <div className="gm-floating-lock-form">
                      <PlayerSearchDropdown value={lockA} allPlayers={allPlayers} usedIds={[lockB?.user_id].filter(Boolean)} onChange={setLockA} />
                      <PlayerSearchDropdown value={lockB} allPlayers={allPlayers} usedIds={[lockA?.user_id].filter(Boolean)} onChange={setLockB} />
                      <button type="button" onClick={lockPair} disabled={!lockA || !lockB}>🔒 Lock</button>
                    </div>
                  </>
                )}
                {floatingMemberModal === 'WAITING' && (
                  <div className="gm-floating-list">
                    {longestWaitingMembers.map((player, index) => (
                      <div key={player.user_id} className="gm-floating-member-row">
                        <b>{index + 1}</b>
                        <span><strong>{player.name}</strong><small>Rank {getHandLevel(player)} • {getMemberStatus(player)}</small></span>
                        <em>รอ {formatElapsedTime(getMemberWaitingTime(player), overviewNow)}</em>
                      </div>
                    ))}
                    {longestWaitingMembers.length === 0 && <p>ยังไม่มีผู้เล่น ACTIVE ที่กำลังรอ</p>}
                  </div>
                )}
              </section>
            </div>
          )}

          <style>{`
            .gm-overview-visibility-row { display:flex; justify-content:flex-end; margin:0 0 10px; }
            .gm-overview-toggle-button { padding:9px 15px; border:1px solid #93c5fd; border-radius:999px; color:#1d4ed8; background:#eff6ff; font-weight:800; cursor:pointer; }
            .gm-floating-member-dock { position:fixed; right:16px; top:130px; z-index:80; display:grid; justify-items:end; gap:7px; }
            .gm-floating-member-toggle { padding:8px 11px; border:1px solid #93c5fd; border-radius:999px; color:#1d4ed8; background:rgba(239,246,255,.98); box-shadow:0 8px 22px rgba(15,23,42,.14); font-weight:900; cursor:pointer; }
            .gm-floating-member-menu { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:8px; width:356px; }
            .gm-floating-member-menu button { padding:9px 12px; border:1px solid #bfdbfe; border-radius:12px; color:#1e3a8a; background:rgba(255,255,255,.96); box-shadow:0 8px 22px rgba(15,23,42,.14); font-weight:800; text-align:left; cursor:pointer; }
            .gm-floating-member-menu button:hover { transform:translateY(-1px); border-color:#3b82f6; }
            .gm-floating-member-menu b { margin-left:5px; padding:1px 6px; border-radius:999px; color:#fff; background:#2563eb; }
            .gm-floating-modal-backdrop { position:fixed; inset:0; z-index:1200; display:grid; place-items:center; padding:20px; background:rgba(15,23,42,.48); }
            .gm-floating-member-modal { width:min(720px,96vw); max-height:86vh; overflow:auto; padding:18px; border:1px solid #bfdbfe; border-radius:18px; background:#fff; box-shadow:0 24px 70px rgba(15,23,42,.28); }
            .gm-floating-modal-heading { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:14px; font-size:20px; }
            .gm-floating-modal-heading button { padding:7px 11px; border:1px solid #fecaca; border-radius:9px; color:#b91c1c; background:#fff1f2; cursor:pointer; }
            .gm-floating-search { display:flex; gap:8px; align-items:center; padding:9px 12px; border:1px solid #cbd5e1; border-radius:10px; }
            .gm-floating-search input { flex:1; min-width:0; border:0; outline:0; }
            .gm-floating-list { display:grid; gap:7px; max-height:55vh; margin-top:12px; overflow:auto; }
            .gm-floating-member-row,.gm-floating-lock-row { display:flex; align-items:center; gap:10px; padding:10px; border:1px solid #e2e8f0; border-radius:10px; background:#f8fafc; }
            .gm-floating-member-row>b { display:grid; place-items:center; width:30px; height:30px; border-radius:9px; color:#0369a1; background:#e0f2fe; }
            .gm-floating-member-row>span { display:grid; flex:1; }
            .gm-floating-member-row small { color:#64748b; }
            .gm-floating-member-row em { color:#b45309; font-style:normal; font-weight:800; }
            .gm-floating-lock-row span { flex:1; }
            .gm-floating-lock-row button { border:0; border-radius:8px; padding:6px 10px; color:#fff; background:#f43f5e; cursor:pointer; }
            .gm-floating-pagination { display:flex; align-items:center; justify-content:center; gap:10px; margin-top:12px; }
            .gm-floating-lock-form { display:grid; grid-template-columns:1fr 1fr auto; gap:8px; margin-top:14px; }
            .gm-floating-lock-form>button { border:0; border-radius:10px; padding:8px 13px; color:#fff; background:#22c55e; font-weight:800; }
            @media(max-width:760px){
              .gm-floating-member-dock {
                position:sticky;
                top:8px;
                right:auto;
                z-index:90;
                justify-items:stretch;
                width:100%;
                margin:8px 0 12px;
              }
              .gm-floating-member-toggle { justify-self:end; }
              .gm-floating-member-menu {
                display:grid;
                grid-template-columns:repeat(4,minmax(0,1fr));
                width:100%;
                max-width:none;
                margin:8px 0 12px;
                padding:6px;
                overflow:visible;
                border:1px solid #dbeafe;
                border-radius:14px;
                background:rgba(248,250,252,.96);
                box-shadow:0 7px 20px rgba(15,23,42,.12);
                backdrop-filter:blur(8px);
              }
              .gm-floating-member-menu button {
                min-width:0;
                padding:8px 5px;
                overflow:hidden;
                border-radius:9px;
                box-shadow:none;
                font-size:11px;
                text-align:center;
                text-overflow:ellipsis;
                white-space:nowrap;
              }
              .gm-floating-member-menu b { margin-left:2px; padding:1px 4px; }
              .gm-floating-lock-form { grid-template-columns:1fr; }
              .gm-floating-modal-backdrop {
                align-items:start;
                padding:12px 10px max(90px,env(safe-area-inset-bottom));
                overflow-y:auto;
              }
              .gm-floating-member-modal {
                width:100%;
                max-height:calc(100dvh - 115px);
                margin-top:8px;
                padding:13px;
                border-radius:15px;
              }
              .gm-floating-modal-heading { position:sticky; top:-13px; z-index:3; padding:8px 0; background:#fff; }
            }
          `}</style>
          {/* ================= MATCHMAKING V2 ================= */}
          <RandomMatchPanel
            groupId={groupId}
            allPlayers={allPlayers}
            lockedPairs={lockedPairs}
            pairMap={pairMap}
            opponentMap={opponentMap}
            queueMatches={queue}
            autoCourtEnabled={autoCourtEnabled}
            availableCourtCount={courts.filter(court => String(court.status || '').toUpperCase() === 'OPEN').length}
            showWaitingAlert
            onCreated={async () => {
              await reloadAll();
            }}
          />
        </>
      )}

      {/* ================= QUEUE ================= */}
      <div
        className={`section-card ${
          isMonitorBoard
            ? 'monitor-board-queue-section'
            : 'dashboard-queue-section'
        }`}
      >
        <style>{`
          .queue-section-heading {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 12px;
          }
          .queue-section-heading h3 { margin: 0; }
          .queue-heading-left { display: flex; min-width: 0; align-items: center; gap: 9px; }
          .queue-bulk-actions { display: flex; flex-wrap: wrap; gap: 8px; }
          .queue-voice-picker {
            min-height: 36px;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px 8px;
            border: 1px solid #c4b5fd;
            border-radius: 10px;
            background: #f5f3ff;
            color: #4c1d95;
            font-size: 12px;
            font-weight: 900;
          }
          .queue-voice-select {
            max-width: 220px;
            min-height: 28px;
            border: 0;
            outline: none;
            background: transparent;
            color: #312e81;
            font: inherit;
            cursor: pointer;
          }
          .queue-bulk-button {
            min-height: 36px;
            padding: 7px 13px;
            border: 0;
            border-radius: 10px;
            color: #fff;
            font-size: 12px;
            font-weight: 900;
            cursor: pointer;
            box-shadow: 0 6px 16px rgba(15, 23, 42, .12);
          }
          .queue-bulk-button.start-all {
            background: linear-gradient(135deg, #16a34a, #22c55e);
          }
          .queue-bulk-button.cancel-all {
            background: linear-gradient(135deg, #dc2626, #f43f5e);
          }
          .queue-bulk-button.voice-toggle.on {
            background: linear-gradient(135deg, #2563eb, #06b6d4);
          }
          .queue-bulk-button.voice-toggle.off {
            color: #475569;
            background: #e2e8f0;
          }
          .queue-bulk-button.voice-test {
            background: linear-gradient(135deg, #7c3aed, #a855f7);
          }
          .queue-bulk-button:disabled {
            cursor: not-allowed;
            opacity: .5;
            box-shadow: none;
          }
          .playing-section-heading {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 10px;
            margin-bottom: 12px;
          }
          .playing-section-heading h3 { margin: 0; }
          .playing-bulk-end-controls {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 7px;
          }
          .playing-select-all,
          .playing-row-selector {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            color: #475569;
            font-size: 11px;
            font-weight: 850;
            cursor: pointer;
            user-select: none;
          }
          .playing-select-all {
            min-height: 36px;
            padding: 6px 10px;
            border: 1px solid #cbd5e1;
            border-radius: 10px;
            background: #fff;
          }
          .playing-select-all input,
          .playing-row-selector input {
            width: 16px;
            height: 16px;
            margin: 0;
            accent-color: #e11d48;
            cursor: pointer;
          }
          .playing-row-selector {
            width: fit-content;
            padding: 3px 7px;
            border: 1px solid #fecdd3;
            border-radius: 999px;
            background: #fff1f2;
            color: #be123c;
          }
          .playing-end-all-button {
            min-height: 36px;
            padding: 7px 13px;
            border: 0;
            border-radius: 10px;
            background: linear-gradient(135deg, #be123c, #ef4444);
            color: #fff;
            font-size: 12px;
            font-weight: 900;
            cursor: pointer;
            box-shadow: 0 6px 16px rgba(190, 18, 60, .2);
          }
          .playing-end-all-button:disabled,
          .playing-select-all:has(input:disabled) {
            cursor: not-allowed;
            opacity: .5;
            box-shadow: none;
          }
          .compact-playing-row.selected-for-end {
            border-color: #fb7185;
            box-shadow: 0 0 0 2px rgba(244, 63, 94, .12);
          }
          .playing-bulk-message {
            margin: 0 0 10px;
            padding: 8px 11px;
            border-radius: 9px;
            font-size: 12px;
            font-weight: 800;
          }
          .playing-bulk-message.success {
            border: 1px solid #86efac;
            background: #f0fdf4;
            color: #166534;
          }
          .playing-bulk-message.error {
            border: 1px solid #fda4af;
            background: #fff1f2;
            color: #be123c;
          }

          @media (max-width: 700px) {
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-queue-scroll {
              display: block;
              width: 100%;
              max-width: 100%;
              min-width: 0;
              padding: 2px 0 7px;
              overflow-x: auto;
              overflow-y: hidden;
              overscroll-behavior-x: contain;
              scrollbar-width: thin;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-queue-list {
              display: grid !important;
              grid-template-columns: 1fr !important;
              gap: 6px;
              width: 650px !important;
              min-width: 650px !important;
              max-width: none !important;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-queue-row {
              display: grid !important;
              grid-template-columns: 58px 165px 16px 165px 226px !important;
              grid-template-areas: "number team-one vs team-two actions" !important;
              align-items: center;
              gap: 4px;
              width: 650px !important;
              min-width: 650px;
              max-width: 650px;
              box-sizing: border-box;
              min-height: 58px;
              padding: 5px;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-queue-number {
              grid-area: number;
              display: grid;
              align-content: center;
              gap: 4px;
              width: auto;
              height: 100%;
              padding: 3px 5px;
              border: 0;
              border-right: 1px solid #e2e8f0;
              font-size: 9px;
              white-space: normal;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-team-one { grid-area: team-one; }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-team-two { grid-area: team-two; }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-vs { grid-area: vs; }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-team {
              width: 165px;
              min-width: 165px;
              min-height: 46px;
              padding: 2px;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-player {
              display: grid !important;
              grid-template-columns: minmax(0,1fr) auto auto !important;
              grid-template-rows: auto auto;
              min-height: 19px;
              padding: 1px 2px;
              font-size: 8px;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-actions {
              grid-area: actions;
              display: grid;
              grid-template-columns: repeat(4,minmax(0,1fr));
              gap: 3px;
              width: 226px;
              margin: 0;
              padding-left: 3px;
              border-left: 1px solid #e2e8f0;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-actions button {
              width: 100%;
              min-height: 23px;
              padding: 2px 3px;
              font-size: 8px;
              white-space: nowrap;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-total,
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-team-label {
              font-size: 7px;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-player-name {
              display: block !important;
              width: 100% !important;
              min-width: 44px !important;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-player .ph-player-history-control {
              display: block !important;
              grid-column: 1 / -1 !important;
              grid-row: 1;
              width: 100% !important;
              min-width: 0 !important;
              overflow: visible !important;
              color: #0f172a !important;
              font-size: 9px !important;
              font-weight: 900 !important;
              line-height: 1.15;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-player .ph-player-trigger {
              display: block !important;
              width: 100% !important;
              min-width: 0 !important;
              overflow: hidden !important;
              color: #0f172a !important;
              text-overflow: ellipsis !important;
              white-space: nowrap !important;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-player .ph-history-open-button {
              display: none !important;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-player-rank {
              grid-column: 2;
              grid-row: 2;
            }
            .gm-container.gm-dashboard-mode .dashboard-queue-section .compact-point {
              grid-column: 3;
              grid-row: 2;
            }

            .gm-container.gm-dashboard-mode .dashboard-playing-section .compact-queue-scroll {
              display: block;
              width: 100%;
              max-width: 100%;
              min-width: 0;
              overflow-x: auto;
              overflow-y: hidden;
              overscroll-behavior-x: contain;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-match-grid {
              display: grid;
              grid-template-columns: 1fr;
              gap: 6px;
              width: 650px;
              min-width: 650px;
              max-width: 650px;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-match-card {
              display: grid !important;
              grid-template-columns: 112px minmax(0,1fr) 150px !important;
              grid-template-rows: auto !important;
              align-items: stretch;
              gap: 4px;
              width: 650px !important;
              max-width: 650px;
              min-width: 650px;
              box-sizing: border-box;
              min-height: 58px;
              padding: 5px;
              border-radius: 9px;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-match-header {
              display: grid !important;
              grid-column: 1 !important;
              grid-row: 1 !important;
              grid-template-columns: minmax(0,1fr) auto;
              align-content: center;
              gap: 3px;
              padding: 2px 4px;
              border-right: 1px solid #e2e8f0;
              overflow: hidden;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-match-number {
              grid-column: 1 / -1;
              font-size: 8px;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-court-badge {
              position: static !important;
              grid-column: 1;
              grid-row: 2;
              display: flex !important;
              justify-content: center;
              width: 42px !important;
              min-width: 42px !important;
              max-width: 42px !important;
              min-height: 22px !important;
              margin: 0 !important;
              padding: 2px 3px !important;
              border-width: 1px;
              border-radius: 6px;
              box-sizing: border-box;
              font-size: 7px;
              overflow: hidden;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-court-badge > span,
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-court-badge small {
              display: none !important;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-court-badge strong {
              font-size: 10px !important;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-header-actions,
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-edit-actions {
              display: grid !important;
              grid-template-columns: 1fr !important;
              gap: 2px;
              position: static !important;
              grid-column: 2;
              grid-row: 2;
              width: 58px !important;
              min-width: 58px;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-header-actions button,
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-edit-actions button {
              min-height: 20px;
              margin: 0 !important;
              padding: 2px !important;
              border-radius: 5px !important;
              font-size: 6px !important;
              line-height: 1 !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-swap-btn,
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-edit-btn {
              min-height: 20px !important;
              padding: 2px !important;
              font-size: 6px !important;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-teams {
              display: grid;
              grid-column: 2 !important;
              grid-row: 1 !important;
              grid-template-columns: minmax(0,1fr) 13px minmax(0,1fr);
              align-items: center;
              gap: 3px;
              min-width: 0;
              width: 100%;
              margin: 0;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-team {
              min-width: 0;
              padding: 2px 3px;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-team-title,
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-player {
              min-height: 18px;
              padding: 1px 2px;
              font-size: 7px;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-player strong {
              display: block;
              min-width: 48px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-referee {
              grid-column: 3 !important;
              grid-row: 1 !important;
              align-self: center;
              min-width: 0;
              margin: 0;
              padding-left: 4px;
              border-left: 1px solid #e2e8f0;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-referee .ref-panel {
              margin: 0;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-referee .ref-title {
              margin: 0 0 3px;
              font-size: 8px;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-referee .ref-actions {
              display: grid !important;
              grid-template-columns: repeat(3,minmax(0,1fr)) !important;
              gap: 2px;
              width: 100%;
            }
            .gm-container.gm-dashboard-mode .dashboard-playing-section .playing-referee .ref-btn {
              width: 100%;
              min-height: 20px;
              padding: 2px;
              border-radius: 5px;
              font-size: 6px;
              line-height: 1.05;
            }
          }
      
        `}</style>

        <div className="queue-section-heading">
          <div className="queue-heading-left">
            <h3>{isMonitorBoard ? '⏳ Match Queue' : '⏳ Queue'}</h3>
            {isMonitorBoard && (
              <RandomMatchPanel
                groupId={groupId}
                allPlayers={allPlayers}
                lockedPairs={lockedPairs}
                pairMap={pairMap}
                opponentMap={opponentMap}
                queueMatches={queue}
                displayMode="monitor-ai"
                onCreated={reloadAll}
              />
            )}
          </div>
          <div className="queue-bulk-actions">
            <label
              className="queue-voice-picker"
              title="เลือกเสียง Google สำหรับประกาศ Match ครั้งถัดไป"
            >
              <span aria-hidden="true">🎙️</span>
              <select
                className="queue-voice-select"
                value={googleTtsVoice}
                disabled={!matchVoiceEnabled}
                onChange={event => setGoogleTtsVoice(event.target.value)}
                aria-label="เลือกเสียงประกาศ Match"
              >
                {googleTtsVoices.map(voice => (
                  <option key={voice.name} value={voice.name}>
                    {voice.label || voice.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className={[
                'queue-bulk-button',
                'voice-toggle',
                matchVoiceEnabled ? 'on' : 'off'
              ].join(' ')}
              onClick={() => setMatchVoiceEnabled(current => !current)}
              aria-pressed={matchVoiceEnabled}
              title="เปิดหรือปิดเสียงเรียกผู้เล่นหลัง Start Match"
            >
              {matchVoiceEnabled
                ? '🔊 เสียงเรียก: เปิด'
                : '🔇 เสียงเรียก: ปิด'}
            </button>
            <button
              type="button"
              className="queue-bulk-button voice-test"
              onClick={testMatchAnnouncement}
              disabled={!matchVoiceEnabled}
              title="ทดลองเสียง Google Cloud และเสียงสำรองของเบราว์เซอร์"
            >
              🔔 ทดสอบเสียง Google
            </button>
            <button
              type="button"
              className={`queue-bulk-button ${autoCourtEnabled ? 'start-all' : ''}`}
              onClick={() => setAutoCourtEnabled(current => !current)}
              aria-pressed={autoCourtEnabled}
              title="นำ Match Queue ลง Match Play อัตโนมัติเมื่อมี Court ว่าง"
            >
              {autoCourtEnabled ? '✅ Auto Court: เปิด' : '⬜ Auto Court: ปิด'}
            </button>
            <button
              type="button"
              className="queue-bulk-button start-all"
              onClick={startAllQueues}
              disabled={
                queue.length === 0 ||
                Boolean(bulkQueueAction) ||
                Boolean(startingId) ||
                Boolean(editingQueueId)
              }
            >
              {bulkQueueAction === 'start'
                ? 'กำลัง Start...'
                : `▶ Start All (${queue.length})`}
            </button>
            <button
              type="button"
              className="queue-bulk-button cancel-all"
              onClick={cancelAllQueues}
              disabled={
                queue.length === 0 ||
                Boolean(bulkQueueAction) ||
                Boolean(startingId) ||
                Boolean(editingQueueId)
              }
            >
              {bulkQueueAction === 'cancel'
                ? 'กำลัง Cancel...'
                : `✕ Cancel All (${queue.length})`}
            </button>
          </div>
        </div>

        {queue.length === 0 && (
          <div style={{ padding: '12px 0', color: '#64748b' }}>No queue</div>
        )}

        <div className="compact-queue-scroll">
          <div className="compact-queue-list">
            {queue.map((m, i) => {
              const team1Total = sumPoints(m.team1);
              const team2Total = sumPoints(m.team2);
              const isEditing = editingQueueId === m.matchId;
              const blockers = getQueueBlockers(m);
              const approvalBlockers = blockers.filter(
                player => getMemberStatus(player) !== 'HOLD'
              );
              const hardBlockers = blockers.filter(
                player => getMemberStatus(player) === 'HOLD'
              );
              const canStart = hardBlockers.length === 0;
              const needsStatusApproval =
                approvalBlockers.length > 0 && canStart;
              const waitingNames = blockers.map(player => player.name).join(', ');

              const renderCompactTeam = (team, teamNumber, total) => {
                const editTeam = teamNumber === 1
                  ? editQueueTeams.team1
                  : editQueueTeams.team2;

                return (
                  <div className={`compact-team compact-team-${teamNumber === 1 ? 'one' : 'two'}`}>
                    <span className="compact-team-label">T{teamNumber}</span>

                    {!isEditing ? (
                      <div className="compact-player-list">
                        {team.map(player => (
                          <span className="compact-player" key={player.user_id}>
                            <PlayerHistoryHover
                              player={player}
                              allPlayers={allPlayers}
                              pairMap={pairMap}
                              opponentMap={opponentMap}
                              className="compact-player-name"
                            />
                            <span
                              className="compact-player-rank"
                              title={`ระดับมือ ${getHandLevel(player)}`}
                            >
                              {getHandLevel(player)}
                            </span>
                            {getMemberStatus(player) === 'PLAY' && (
                              <span className="compact-player-play">PLAY</span>
                            )}
                            <span className="compact-point">{player.point}</span>
                            {getMemberStatus(player) === 'PLAY' && player.playingMatchId && (
                              <span
                                className="compact-player-playing-match"
                                aria-label={`${player.name} กำลังแข่งขันอยู่ Match #${player.playingGroupMatchNo || player.playingMatchId}`}
                                style={{
                                  position: 'static',
                                  display: 'block',
                                  gridColumn: '1 / -1',
                                  width: '100%',
                                  marginTop: 3,
                                  whiteSpace: 'normal',
                                  overflowWrap: 'anywhere'
                                }}
                              >
                                <b>{player.name}</b>{' • '}กำลังแข่ง Match #{player.playingGroupMatchNo || player.playingMatchId}
                                {player.playingCourtNo
                                  ? ` • Court ${player.playingCourtNo}`
                                  : ''}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="compact-edit-area">
                        {editTeam.map((player, playerIndex) => (
                          <PlayerSearchDropdown
                            key={playerIndex}
                            value={player}
                            allPlayers={allPlayers}
                            usedIds={[
                              ...editQueueTeams.team1.map(x => x?.user_id),
                              ...editQueueTeams.team2.map(x => x?.user_id)
                            ].filter(id => id && id !== player?.user_id)}
                            onChange={newPlayer => {
                              const key = teamNumber === 1 ? 'team1' : 'team2';
                              const updatedTeam = [...editQueueTeams[key]];
                              updatedTeam[playerIndex] = newPlayer;
                              setEditQueueTeams({ ...editQueueTeams, [key]: updatedTeam });
                            }}
                          />
                        ))}
                      </div>
                    )}

                    <span className="compact-total">Σ {total}</span>
                  </div>
                );
              };

              return (
                <div className={`compact-queue-row ${isEditing ? 'editing' : ''}`} key={m.matchId}>
                  <div className="compact-queue-number">
                    <strong>Match #{getMatchDisplayNo(m)}</strong>
                    {needsStatusApproval ? (
                      <span
                        className="compact-approve"
                        title={`ต้อง Approve: ${waitingNames}`}
                      >
                        APPROVE
                      </span>
                    ) : canStart ? (
                      <span className="compact-ready">READY</span>
                    ) : (
                      <span
                        className="compact-waiting"
                        title={`รอผู้เล่น: ${waitingNames}`}
                      >
                        WAITING
                      </span>
                    )}
                  </div>

                  {renderCompactTeam(m.team1, 1, team1Total)}
                  <span className="compact-vs">VS</span>
                  {renderCompactTeam(m.team2, 2, team2Total)}

                  <div className="compact-actions">
                    {!isEditing ? (
                      <>
                        <button
                          className="btn-edit"
                          disabled={
                            Boolean(bulkQueueAction) ||
                            Boolean(swappingPairKey)
                          }
                          onClick={() => startEditQueue(m)}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          type="button"
                          className="btn-swap-pair"
                          disabled={
                            Boolean(bulkQueueAction) ||
                            Boolean(swappingPairKey)
                          }
                          title="หมุนสลับผู้เล่นทั้ง 4 คน และเปลี่ยนคู่ทุกครั้ง"
                          onClick={() => swapMatchPartners(m, 'queue')}
                        >
                          {swappingPairKey === `queue-${m.matchId}`
                            ? 'กำลังสลับ...'
                            : isMonitorBoard
                              ? '🔄 คู่'
                              : '🔄 สลับคู่'}
                        </button>
                        <button
                          className="btn-start"
                          disabled={
                            !canStart ||
                            Boolean(bulkQueueAction) ||
                            Boolean(swappingPairKey) ||
                            startingId === m.matchId
                          }
                          title={
                            !canStart
                              ? `รอผู้เล่น: ${waitingNames}`
                              : needsStatusApproval
                                ? `ตรวจสอบสถานะของ ${waitingNames} และ Approve อีกครั้ง`
                                : 'เริ่ม Match'
                          }
                          onClick={() => startMatch(m)}
                        >
                          {startingId === m.matchId
                            ? 'Starting...'
                            : needsStatusApproval
                              ? '⚠ Approve Start'
                              : canStart
                              ? '▶ Start'
                              : '⏳ รอผู้เล่น'}
                        </button>
                        <button
                          className="btn-cancel"
                          disabled={
                            Boolean(bulkQueueAction) ||
                            Boolean(swappingPairKey)
                          }
                          onClick={() => cancelQueue(m.matchId)}
                        >
                          ✕ Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn-save" onClick={saveQueueEdit}>💾 Save</button>
                        <button className="btn-cancel" onClick={() => setEditingQueueId(null)}>✕ Cancel</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {false && queue.map((m, i) => {

          const team1Total = m.team1.reduce((sum, p) => sum + (p.point || 0), 0);
          const team2Total = m.team2.reduce((sum, p) => sum + (p.point || 0), 0);
          const isEditing = editingQueueId === m.matchId;





          const canStart = true;
          const reason = '';


          return (
            <div
              key={m.matchId}
              className={`queue-card ${isEditing ? 'editing' : ''}`}
            >

              {/* ================= HEADER ================= */}
              <div className="match-header">

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <b>Match #{getMatchDisplayNo(m)}</b>

                  {canStart ? (
                    <span className="badge-ready">READY</span>
                  ) : (
                    <span className="badge-waiting">WAITING</span>
                  )}
                </div>
{!isEditing ? (

  <div className="match-actions">

    <button
      type="button"
      className="btn-swap-team"
      onClick={() => swapPlayingTeams(m)}
    >
      🔄 สลับคู่
    </button>

    <button
      type="button"
      className="btn-edit"
      onClick={() =>
        startEditPlayingMatch(m)
      }
    >
      ✏️ Edit
    </button>

  

                    <button
                      className="btn-start"
                      disabled={startingId === m.matchId}
                      onClick={() => startMatch(m)}
                      style={{
                        marginLeft: 8
                      }}
                    >
                      {startingId === m.matchId
                        ? 'Starting...'
                        : '▶ Start'}
                    </button>


                    <button
                      className="btn-cancel"
                      onClick={() => cancelQueue(m.matchId)}
                      style={{ marginLeft: 8 }}
                    >
                      ✖ Cancel
                    </button>

                  </div>
                ) : (
                  <div>
                    <button
                      className="btn-save"
                      onClick={saveQueueEdit}
                    >
                      💾 Save
                    </button>

                    <button
                      className="btn-cancel"
                      onClick={() => setEditingQueueId(null)}
                      style={{ marginLeft: 8 }}
                    >
                      ❌ Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* ================= REASON ================= */}
              {!canStart && (
                <div className="start-reason">
                  ⚠ {reason}
                </div>
              )}

              {/* ================= TEAMS ================= */}
              <div className="queue-teams">

                {/* TEAM 1 */}
                <div className="team-box team1-edit">
                  <div className="team-title">
                    🟦 Team 1
                    <span className="team-total">รวม {team1Total}</span>
                  </div>

                  {!isEditing ? (
                    m.team1.map((p, idx) => {

                      const idle = getIdleMinutes(p.last_finished_at);

                      const teammate = m.team1.find(x => x.user_id !== p.user_id);
                      const pairCount = teammate ? getPairCount(p, teammate) : 0;

                      // ?? ??????????????
                      const opponents = m.team2
                        .map(op => ({
                          name: op.name,
                          count: getOpponentCount(p, op)
                        }))
                        .filter(x => x.count > 0);

                      return (
                        <div key={idx} className="player-row">

                          {/* ?? ???????? */}
                          <div className="player-top">
                            <span>{p.name}</span>
                            <span className="point-badge">{p.point}</span>
                          </div>

                          {/* ?? ?????? status */}
                          <div className="player-meta">
                            G {p.totalMatches}
                            <span className={`status-badge status-${p.memberStatus?.toLowerCase()}`}>
                              {p.memberStatus}
                            </span>
                            <span className="idle-badge">{idle}m</span>
                          </div>

                          {/* ?? history */}
                          {pairCount > 0 && (
                            <div className="pair-history">
                              คู่กับ {teammate.name} ({pairCount})
                            </div>
                          )}

                          {opponents.length > 0 && (
                            <div className="opponent-history">
                              ตรงข้าม {opponents.map(o => `${o.name}(${o.count})`).join(', ')}
                            </div>
                          )}

                        </div>
                      );
                    })
                  ) : (
                    editQueueTeams.team1.map((p, idx) => (
                      <div key={idx} className="edit-player-row">
                        <PlayerSearchDropdown
                          value={p}
                          allPlayers={allPlayers}
                          usedIds={[
                            ...editQueueTeams.team1.map(x => x?.user_id),
                            ...editQueueTeams.team2.map(x => x?.user_id)
                          ].filter(id => id && id !== p?.user_id)}
                          onChange={np => {
                            const t1 = [...editQueueTeams.team1];
                            t1[idx] = np;
                            setEditQueueTeams({ ...editQueueTeams, team1: t1 });
                          }}
                        />
                      </div>
                    ))
                  )}

                </div>

                {/* TEAM 2 */}
                <div className="team-box team2-edit">
                  <div className="team-title">
                    🟥 Team 2
                    <span className="team-total">รวม {team2Total}</span>
                  </div>

                  {!isEditing ? (
                    m.team2.map((p, idx) => {

                      const idle = getIdleMinutes(p.last_finished_at);

                      const teammate = m.team2.find(x => x.user_id !== p.user_id);
                      const pairCount = teammate ? getPairCount(p, teammate) : 0;

                      // ?? ??????????????
                      const opponents = m.team1
                        .map(op => ({
                          name: op.name,
                          count: getOpponentCount(p, op)
                        }))
                        .filter(x => x.count > 0);

                      return (
                        <div key={idx} className="player-row">

                          <span>
                            {p.name}

                            <span className="games-badge">
                              G {p.totalMatches || 0}
                            </span>

                            <span className={`status-badge status-${p.memberStatus?.toLowerCase()}`}>
                              {p.memberStatus}
                            </span>

                            <span className={`idle-badge ${idle > 20 ? 'idle-very-high' : idle > 10 ? 'idle-high' : ''
                              }`}>
                              {idle}m
                            </span>
                          </span>

                          <span className="point-badge">{p.point}</span>

                          {/* ?? teammate */}
                          {pairCount > 0 && teammate && (
                            <div className="pair-history">
                              คู่กับ {teammate.name} ({pairCount} ครั้ง)
                            </div>
                          )}

                          {/* ?? opponents */}
                          {opponents.length > 0 && (
                            <div className="opponent-history">
                              ตรงข้าม
                              {opponents.map(o => ` ${o.name} (${o.count})`).join(', ')}
                            </div>
                          )}

                        </div>
                      );
                    })
                  ) : (
                    editQueueTeams.team2.map((p, idx) => (
                      <div key={idx} className="edit-player-row">
                        <PlayerSearchDropdown
                          value={p}
                          allPlayers={allPlayers}
                          usedIds={[
                            ...editQueueTeams.team1.map(x => x?.user_id),
                            ...editQueueTeams.team2.map(x => x?.user_id)
                          ].filter(id => id && id !== p?.user_id)}
                          onChange={np => {
                            const t2 = [...editQueueTeams.team2];
                            t2[idx] = np;
                            setEditQueueTeams({ ...editQueueTeams, team2: t2 });
                          }}
                        />
                      </div>
                    ))
                  )}

                </div>

              </div>
            </div>
          );
        })}
      </div>



      {/* ================= MATCH STATUS ================= */}
      {/* ================= MATCH STATUS ================= */}
      <div
        className={`section-card match-status-section ${
          isMonitorBoard
            ? 'monitor-board-playing-section'
            : 'dashboard-playing-section'
        }`}
      >

        <div className="playing-section-heading">
          <h3>
            {isMonitorBoard
              ? '🏸 Match Start (Playing)'
              : '📊 Match Status (Playing)'}
          </h3>

          <div className="playing-bulk-end-controls">
            <label className="playing-select-all">
              <input
                type="checkbox"
                checked={allPlayingMatchesSelected}
                disabled={bulkEndingMatches || playingMatchesForBulk.length === 0}
                onChange={event => {
                  setSelectedEndMatchIds(
                    event.target.checked
                      ? playingMatchesForBulk.map(match => String(match.matchId))
                      : []
                  );
                  setBulkEndMessage(null);
                }}
              />
              เลือกทั้งหมด ({playingMatchesForBulk.length})
            </label>

            <button
              type="button"
              className="playing-end-all-button"
              disabled={
                bulkEndingMatches ||
                selectedPlayingMatches.length === 0
              }
              onClick={endSelectedPlayingMatches}
            >
              {bulkEndingMatches
                ? 'กำลัง End Match...'
                : `🤝 End Match เป็น DRAW (${selectedPlayingMatches.length})`}
            </button>
          </div>
        </div>

        {bulkEndMessage && (
          <div
            className={`playing-bulk-message ${bulkEndMessage.type}`}
            role="status"
            aria-live="polite"
          >
            {bulkEndMessage.text}
          </div>
        )}

        {matches.filter(m => m.status === 'playing').length === 0 && (
          <div style={{ padding: '12px 0', color: '#64748b' }}>No match is playing</div>
        )}

        <div className="compact-queue-scroll">
     <div className="playing-match-grid">

 {sortedPlayingMatches.map(m => {

  const team1Players =
    m.players.filter(
      p => Number(p.team) === 1
    );

  const team2Players =
    m.players.filter(
      p => Number(p.team) === 2
    );

  const team1Total =
    team1Players.reduce(
      (sum, p) =>
        sum + Number(p.point || 0),
      0
    );

  const team2Total =
    team2Players.reduce(
      (sum, p) =>
        sum + Number(p.point || 0),
      0
    );

  const isEditing =
    editingMatchId === m.matchId;

  const isRecent =
    isRecentlyStartedMatch(m);

  return (
    <div
      key={m.matchId}
      className={[
        'playing-match-card',
        isEditing
          ? 'editing'
          : '',
        isRecent
          ? 'recently-started'
          : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >

      {/* ================= HEADER ================= */}

      <div className="playing-match-header">

        <div className="playing-match-number">

          Match #{getMatchDisplayNo(m)}

          {isRecent && (
            <span className="playing-new-match-badge">
              🔥 เพิ่งเริ่ม
            </span>
          )}

          {Number(m.reopenCount || m.reopen_count || 0) > 0 && (
            <span
              className="playing-reopen-match-badge"
              title={`Re-open ล่าสุด ${m.reopenedAt || m.reopened_at ? new Date(m.reopenedAt || m.reopened_at).toLocaleString('th-TH') : '-'}`}
            >
              ↩ RE-OPEN {Number(m.reopenCount || m.reopen_count) > 1 ? `×${Number(m.reopenCount || m.reopen_count)}` : ''}
            </span>
          )}

        </div>

        <div className="playing-court-badge">

          <span>🏸</span>

          <div>
            <small>COURT</small>

            <strong>
              {m.court_no ||
                m.courtNo ||
                '-'}
            </strong>
          </div>

        </div>

        {/* ส่วนปุ่มเดิมของคุณต่อจากตรงนี้ */}

     

          {!isEditing ? (

  <div className="playing-header-actions">

    <button
      type="button"
      className="btn-swap-pair playing-swap-btn"
      disabled={Boolean(swappingPairKey)}
      onClick={() =>
        swapMatchPartners(m, 'playing')
      }
      title="สลับคู่ผู้เล่นใน Match นี้"
    >
      {swappingPairKey === `playing-${m.matchId}`
        ? '⏳ กำลังสลับ...'
        : '🔄 สลับคู่'}
    </button>

    <button
      type="button"
      className="btn-edit playing-edit-btn"
      disabled={Boolean(swappingPairKey)}
      onClick={() =>
        startEditPlayingMatch(m)
      }
    >
      ✏️ Edit
    </button>

    <button
      type="button"
      className="btn-edit playing-move-court-btn"
      disabled={Boolean(swappingPairKey)}
      onClick={() => openMoveCourt(m)}
      title="ย้าย Match นี้ไปคอร์ทอื่น"
    >
      🏟 ย้ายคอร์ท
    </button>

  </div>

) : (
              <div className="playing-edit-actions">

                <button
                  className="btn-save"
                  onClick={savePlayingPlayers}
                >
                  💾 Save
                </button>

                <button
                  className="btn-cancel"
                  onClick={() =>
                    setEditingMatchId(null)
                  }
                >
                  ❌
                </button>

              </div>
            )}

          </div>


          {/* ================= TEAM ================= */}

          <div className="playing-teams">

            {/* TEAM 1 */}

            <div className="playing-team team-one">

              <div className="playing-team-title">
                <span>🟦 Team 1</span>
                <b>Σ {team1Total}</b>
              </div>

              {!isEditing ? (

                team1Players.map(p => (

                  <div
                    key={p.user_id}
                    className="playing-player"
                  >
                    <strong>
                      {p.name}
                    </strong>

                    <span>
                      {p.level_rank || ''}
                    </span>

                    <b>
                      {p.point}
                    </b>
                  </div>

                ))

              ) : (

                editTeams.team1.map((p, idx) => (

                  <PlayerSearchDropdown
                    key={idx}
                    value={p}
                    allPlayers={allPlayers}
                    usedIds={[
                      ...editTeams.team1.map(
                        x => x?.user_id
                      ),
                      ...editTeams.team2.map(
                        x => x?.user_id
                      )
                    ].filter(
                      id =>
                        id &&
                        id !== p?.user_id
                    )}
                    onChange={np => {

                      const t1 =
                        [...editTeams.team1];

                      t1[idx] = np;

                      setEditTeams({
                        ...editTeams,
                        team1: t1
                      });

                    }}
                  />

                ))

              )}

            </div>


            <div className="playing-vs">
              VS
            </div>


            {/* TEAM 2 */}

            <div className="playing-team team-two">

              <div className="playing-team-title">
                <span>🟥 Team 2</span>
                <b>Σ {team2Total}</b>
              </div>

              {!isEditing ? (

                team2Players.map(p => (

                  <div
                    key={p.user_id}
                    className="playing-player"
                  >
                    <strong>
                      {p.name}
                    </strong>

                    <span>
                      {p.level_rank || ''}
                    </span>

                    <b>
                      {p.point}
                    </b>
                  </div>

                ))

              ) : (

                editTeams.team2.map((p, idx) => (

                  <PlayerSearchDropdown
                    key={idx}
                    value={p}
                    allPlayers={allPlayers}
                    usedIds={[
                      ...editTeams.team1.map(
                        x => x?.user_id
                      ),
                      ...editTeams.team2.map(
                        x => x?.user_id
                      )
                    ].filter(
                      id =>
                        id &&
                        id !== p?.user_id
                    )}
                    onChange={np => {

                      const t2 =
                        [...editTeams.team2];

                      t2[idx] = np;

                      setEditTeams({
                        ...editTeams,
                        team2: t2
                      });

                    }}
                  />

                ))

              )}

            </div>

          </div>


          {/* ================= REFEREE ================= */}

          {!isEditing && (

            <div className="playing-referee">

              <RefereePanel
                matchId={m.matchId}
                groupId={groupId}
                onFinish={loadMatches}
              />

            </div>

          )}

        </div>
      );

    })}

</div>
        </div>
      </div>



      {movingCourtMatch && createPortal(
        <div
          className="court-move-backdrop"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) closeMoveCourt();
          }}
        >
          <div
            className="court-move-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="court-move-title"
          >
            <div className="court-move-heading">
              <div>
                <h3 id="court-move-title">
                  🏟 ย้าย Match #{getMatchDisplayNo(movingCourtMatch)}
                </h3>
                <p>
                  คอร์ทปัจจุบัน Court {movingCourtMatch.court_no || '-'} —
                  เลือกคอร์ทใหม่ได้ตั้งแต่ 1–18
                </p>
              </div>
              <button
                type="button"
                className="court-move-close"
                onClick={closeMoveCourt}
                disabled={movingCourt}
                aria-label="ปิดหน้าต่าง"
              >
                ✕
              </button>
            </div>

            <div className="court-move-grid">
              {Array.from({ length: 18 }, (_, index) => index + 1)
                .map(courtNo => {
                  const court = courts.find(
                    item => Number(item.court_no) === courtNo
                  );
                  const occupiedMatch = matches.find(
                    item =>
                      String(item.status || '').toLowerCase() === 'playing' &&
                      Number(item.court_no) === courtNo
                  );
                  const isCurrent =
                    Number(movingCourtMatch.court_no) === courtNo;
                  const isSelected = Number(targetCourtNo) === courtNo;
                  const isOccupied = Boolean(occupiedMatch) && !isCurrent;
                  const isMissing = !court;

                  return (
                    <button
                      type="button"
                      key={courtNo}
                      className={[
                        'court-move-option',
                        isCurrent ? 'is-current' : '',
                        isSelected ? 'is-selected' : '',
                        isOccupied ? 'is-occupied' : ''
                      ].filter(Boolean).join(' ')}
                      disabled={isMissing || isCurrent || movingCourt}
                      onClick={() => setTargetCourtNo(courtNo)}
                      title={
                        isMissing
                          ? `ยังไม่ได้เพิ่ม Court ${courtNo}`
                          : isCurrent
                            ? 'คอร์ทปัจจุบัน'
                            : occupiedMatch
                              ? `มี Match #${getMatchDisplayNo(occupiedMatch)} — เลือกเพื่อสลับ`
                              : `ย้ายไป Court ${courtNo}`
                      }
                    >
                      <span>COURT</span>
                      <strong>{courtNo}</strong>
                      <small>
                        {isMissing
                          ? 'ยังไม่ได้เพิ่ม'
                          : isCurrent
                            ? 'คอร์ทปัจจุบัน'
                            : occupiedMatch
                              ? `Match #${getMatchDisplayNo(occupiedMatch)} • สลับ`
                              : String(court.status || 'OPEN').toUpperCase()}
                      </small>
                    </button>
                  );
                })}
            </div>

            <div className="court-move-legend">
              <span>🟦 เลือกแล้ว</span>
              <span>🟡 คอร์ทปัจจุบัน</span>
              <span>🔴 มี Match — ต้อง Approve เพื่อสลับ</span>
              <span>⚪ ยังไม่ได้เพิ่ม — เลือกไม่ได้</span>
            </div>

            <div className="court-move-actions">
              <button
                type="button"
                className="secondary"
                onClick={closeMoveCourt}
                disabled={movingCourt}
              >
                ยกเลิก
              </button>
              <button
                type="button"
                className="primary"
                onClick={submitMoveCourt}
                disabled={!targetCourtNo || movingCourt}
              >
                {movingCourt
                  ? 'กำลังย้าย...'
                  : targetCourtNo
                    ? `ย้ายไป Court ${targetCourtNo}`
                    : 'เลือกคอร์ทใหม่'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );   // ปิด return

}      // ปิด function GameMatch

export default GameMatch;
